import {
	Activity,
	AlertCircle,
	ArrowLeft,
	ArrowRight,
	BookOpen,
	Brain,
	Briefcase,
	Calendar,
	DollarSign,
	Edit,
	Heart,
	Home,
	Lightbulb,
	Loader2,
	Plus,
	RefreshCw,
	Shield,
	Smile,
	Sparkles,
	Target,
	TrendingUp,
	Users,
	Zap,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
	useLocation,
	useNavigate,
	useParams,
	useSearchParams,
} from "react-router-dom";
import { AssignedTopics } from "@/components/AssignedTopics";
import { DraftReflectionsList } from "@/components/DraftReflectionsList";
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Input,
	Progress,
	Switch,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	Textarea,
} from "@/components/primitives";
import { useAuth } from "@/contexts/AuthContext";
import { aiService } from "@/lib/ai-service";
import { supabase } from "@/lib/supabase";
import {
	ContributingFactorsIntroStep,
	ContributingFactorsStep,
	ConversationEndSummary,
	CorrectiveActionsIntroStep,
	CorrectiveActionsStep,
	DescriptionStep,
	FutureStateStep,
	LinkingStep,
	QAStep,
	SignalsIntroStep,
	SignalsStep,
	SummaryStep,
} from "./ReflectionFlow/components";
import {
	REFLECTION_CONSTANTS,
	REFLECTION_STEPS,
	type ReflectionStep as ReflectionStepType,
} from "./ReflectionFlow/constants";
import {
	useReflectionPersistenceExtended,
	useStepNavigation,
} from "./ReflectionFlow/hooks";
import {
	loadActionLinks as loadActionLinksService,
	saveActionLinks as saveActionLinksService,
} from "./ReflectionFlow/services/reflectionService";
import { getSelectedCount as getSelectedCountFactors } from "./ReflectionFlow/utils/contributingFactorsUtils";
import { getSelectedCount as getSelectedCountActions } from "./ReflectionFlow/utils/correctiveActionsUtils";
import { debugError, debugLog, debugWarn } from "./ReflectionFlow/utils/debug";
import { initializeActionLinks } from "./ReflectionFlow/utils/linkingUtils";
import { extractQuestionText } from "./ReflectionFlow/utils/qaUtils";
import { getSelectedCount as getSelectedCountSignals } from "./ReflectionFlow/utils/signalsUtils";

// Use ReflectionStep type from constants
type ReflectionStep = ReflectionStepType;

interface ContributingFactor {
	id: string;
	title: string;
	description: string;
	selected: boolean;
	isEditing?: boolean;
}

interface Signal {
	id: string;
	title: string;
	description: string;
	selected: boolean;
	isEditing?: boolean;
}

interface CorrectiveAction {
	id: string;
	title: string;
	description: string;
	selected: boolean;
	isEditing?: boolean;
}

interface SuggestedReflection {
	title: string;
	description: string;
	reasoning: string;
}

interface ActionLink {
	correctiveActionId: string;
	linkedFactors: string[];
	linkedSignals: string[];
}

interface AssignedTopic {
	topic_id: string;
	title: string;
	description: string;
	initial_question: string;
	assignment_id: string;
}

interface ReflectionTheme {
	id: string;
	name: string;
	description: string;
	prompt: string;
	is_default: boolean;
	sort_order: number;
}

// Helper function to get appropriate icon for a theme based on its name
const getThemeIcon = (themeName: string) => {
	const name = themeName.toLowerCase();

	// Work/Career related
	if (
		name.includes("work") ||
		name.includes("career") ||
		name.includes("job") ||
		name.includes("professional")
	) {
		return Briefcase;
	}
	// Relationships
	if (
		name.includes("relationship") ||
		name.includes("family") ||
		name.includes("friend") ||
		name.includes("social")
	) {
		return Users;
	}
	// Health/Wellness
	if (
		name.includes("health") ||
		name.includes("wellness") ||
		name.includes("fitness") ||
		name.includes("exercise") ||
		name.includes("physical")
	) {
		return Activity;
	}
	// Finance/Money
	if (
		name.includes("finance") ||
		name.includes("money") ||
		name.includes("financial") ||
		name.includes("budget") ||
		name.includes("wealth")
	) {
		return DollarSign;
	}
	// Goals/Achievement
	if (
		name.includes("goal") ||
		name.includes("achievement") ||
		name.includes("success") ||
		name.includes("ambition")
	) {
		return Target;
	}
	// Growth/Development
	if (
		name.includes("growth") ||
		name.includes("development") ||
		name.includes("learning") ||
		name.includes("improvement") ||
		name.includes("progress")
	) {
		return TrendingUp;
	}
	// Personal/Self
	if (
		name.includes("personal") ||
		name.includes("self") ||
		name.includes("identity") ||
		name.includes("values")
	) {
		return Sparkles;
	}
	// Stress/Anxiety
	if (
		name.includes("stress") ||
		name.includes("anxiety") ||
		name.includes("worry") ||
		name.includes("pressure") ||
		name.includes("overwhelm")
	) {
		return AlertCircle;
	}
	// Happiness/Joy
	if (
		name.includes("happiness") ||
		name.includes("joy") ||
		name.includes("positive") ||
		name.includes("gratitude")
	) {
		return Smile;
	}
	// Home/Life
	if (
		name.includes("home") ||
		name.includes("life") ||
		name.includes("lifestyle") ||
		name.includes("balance")
	) {
		return Home;
	}
	// Time/Productivity
	if (
		name.includes("time") ||
		name.includes("productivity") ||
		name.includes("efficiency") ||
		name.includes("schedule")
	) {
		return Calendar;
	}
	// Energy/Motivation
	if (
		name.includes("energy") ||
		name.includes("motivation") ||
		name.includes("drive") ||
		name.includes("passion")
	) {
		return Zap;
	}
	// Protection/Safety
	if (
		name.includes("safety") ||
		name.includes("security") ||
		name.includes("protection") ||
		name.includes("stability")
	) {
		return Shield;
	}
	// Knowledge/Education
	if (
		name.includes("knowledge") ||
		name.includes("education") ||
		name.includes("study") ||
		name.includes("learning")
	) {
		return BookOpen;
	}
	// Love/Emotions
	if (
		name.includes("love") ||
		name.includes("emotion") ||
		name.includes("feeling") ||
		name.includes("heart")
	) {
		return Heart;
	}

	// Default icon
	return Lightbulb;
};

const VALID_STEPS: ReflectionStep[] = [
	"intro",
	"questions",
	"description",
	"contributing-factors-intro",
	"contributing-factors",
	"signals-intro",
	"signals",
	"future-state",
	"corrective-actions-intro",
	"corrective-actions",
	"linking",
	"summary",
];

export function ReflectionFlow() {
	const { user } = useAuth();
	const location = useLocation();
	const { id: draftIdFromParams } = useParams<{ id?: string }>();
	const draftIdFromPath = (() => {
		// Tests sometimes render ReflectionFlow without route matching, so useParams() can be empty.
		// Fall back to parsing the pathname for known ReflectionFlow entry patterns.
		const continueMatch = location.pathname.match(
			/^\/reflection\/continue\/([^/?#]+)/,
		);
		if (continueMatch?.[1]) return continueMatch[1];

		// Legacy/test-only pattern: rendering ReflectionFlow directly at /reflection/<id>
		const legacyMatch = location.pathname.match(/^\/reflection\/([^/?#]+)/);
		const legacyId = legacyMatch?.[1];
		if (legacyId && legacyId !== "new" && legacyId !== "continue")
			return legacyId;

		return undefined;
	})();
	const draftId = draftIdFromParams ?? draftIdFromPath;
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const [currentStep, setCurrentStep] = useState<ReflectionStep>("intro");
	// Synchronous ref to read the latest step during async operations (avoid stale closure)
	const currentStepRef = useRef<ReflectionStep>("intro");
	useEffect(() => {
		currentStepRef.current = currentStep;
	}, [currentStep]);
	const [isRestoringFromUrl, setIsRestoringFromUrl] = useState(false);
	const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
	const [currentReflectionId, setCurrentReflectionId] = useState<string | null>(
		null,
	);
	// Keep a synchronous reference to the reflection ID to avoid race conditions where state hasn't updated yet
	const reflectionIdRef = useRef<string | null>(null);
	const [isDraft, setIsDraft] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [lastSaved, setLastSaved] = useState<Date | null>(null);

	// Topic assignment state
	const [assignedTopic, setAssignedTopic] = useState<AssignedTopic | null>(
		null,
	);
	const [topicAssignmentId, setTopicAssignmentId] = useState<string | null>(
		null,
	);

	// Reflection themes state
	const [reflectionThemes, setReflectionThemes] = useState<ReflectionTheme[]>(
		[],
	);
	const [selectedTheme, setSelectedTheme] = useState<ReflectionTheme | null>(
		null,
	);

	// Q&A State
	const [currentQuestion, setCurrentQuestion] = useState("");
	const [currentAnswer, setCurrentAnswer] = useState("");
	const [qaHistory, setQaHistory] = useState<
		Array<{ question: string; answer: string }>
	>([]);
	const [conversationHistory, setConversationHistory] = useState<
		Array<{ role: string; content: string }>
	>([]);
	const [isLoadingQuestion, setIsLoadingQuestion] = useState(false);
	const [isProcessingSummary, setIsProcessingSummary] = useState(false);
	const [aiError, setAiError] = useState<string | null>(null);
	const [decisionSummary, setDecisionSummary] = useState<{
		title: string;
		description: string;
	} | null>(null);
	const [conversationEndSummary, setConversationEndSummary] = useState<{
		title?: string;
		description: string;
	} | null>(null);
	const [isDivingDeeper, setIsDivingDeeper] = useState(false);

	// Conversation auto-scroll ref
	const conversationRef = useRef<HTMLDivElement>(null);
	// Prevent concurrent save operations (avoids duplicate reflection creation)
	const saveInProgressRef = useRef(false);
	// Mutex to serialize save operations across renders
	const saveMutexRef = useRef<Promise<void> | null>(null);
	// Prevent duplicate AI initiation
	const beginQuestionsInProgressRef = useRef(false);
	// Prevent duplicate reflection completion
	const hasAutoCompletedRef = useRef(false);
	// Prevent draft reload during AI summary transition
	const isTransitioningFromAIRef = useRef(false);

	// Use step navigation hook (after state declarations)
	const {
		navigateToStep: navigateToStepHook,
		goNext: goNextHook,
		goBack: goBackHook,
		canGoNext,
		canGoBack,
	} = useStepNavigation({
		currentStep,
		setCurrentStep,
		reflectionId: currentReflectionId,
	});

	// Auto-scroll to bottom when conversation updates
	useEffect(() => {
		if (conversationRef.current) {
			conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
		}
	}, [qaHistory, currentQuestion, isLoadingQuestion]);

	// Auto-start questions when on questions step but no current question exists
	const hasAutoStartedQuestionsRef = useRef(false);
	// Track when user is navigating back to prevent URL sync interference
	const isNavigatingBackRef = useRef(false);
	useEffect(() => {
		const isAISuggested = searchParams.get("suggested") === "true";
		if (
			currentStep === "questions" &&
			!currentQuestion &&
			!isLoadingQuestion &&
			!aiError &&
			user &&
			!isRestoringFromUrl &&
			!isAISuggested && // Avoid double-start when suggested flow already starts it
			!hasAutoStartedQuestionsRef.current
		) {
			console.log(
				"Auto-starting questions - no current question found on questions step (deterministic)",
				{
					isFresh: searchParams.get("fresh") === "1",
					hasSelectedTheme: !!selectedTheme,
				},
			);
			hasAutoStartedQuestionsRef.current = true;
			// Use selectedTheme which was set in handleCreateNew before navigation
			beginQuestions(selectedTheme || undefined);
		}
	}, [
		currentStep,
		currentQuestion,
		isLoadingQuestion,
		aiError,
		user,
		isRestoringFromUrl,
		searchParams,
		selectedTheme,
	]);

	// Auto-complete reflection when reaching summary step
	useEffect(() => {
		debugLog("🔍 Auto-completion check:", {
			currentStep,
			currentReflectionId,
			isDraft,
			hasAutoCompleted: hasAutoCompletedRef.current,
			isSaving,
		});

		if (
			currentStep === "summary" &&
			currentReflectionId &&
			isDraft &&
			!hasAutoCompletedRef.current &&
			!isSaving
		) {
			console.log(
				"✨ AUTO-COMPLETION CONDITIONS MET - Starting auto-completion",
			);
			hasAutoCompletedRef.current = true;

			// Small delay to ensure state has settled before completion
			setTimeout(() => {
				completeReflection()
					.then(() => {
						console.log("✅ Reflection auto-completion finished successfully");
					})
					.catch((error) => {
						console.error("❌ Error during auto-completion:", error);
						// Reset flag on error so user can manually complete if needed
						hasAutoCompletedRef.current = false;
					});
			}, 100);
		} else if (currentStep === "summary") {
			console.log("⚠️ On summary step but auto-completion skipped because:", {
				noReflectionId: !currentReflectionId,
				notDraft: !isDraft,
				alreadyCompleted: hasAutoCompletedRef.current,
				currentlySaving: isSaving,
			});
		}
	}, [currentStep, currentReflectionId, isDraft, isSaving]);

	// Description State
	const [reflectionDescription, setReflectionDescription] = useState("");
	const [reflectionTitle, setReflectionTitle] = useState("");

	// Contributing Factors State
	const [contributingFactors, setContributingFactors] = useState<
		ContributingFactor[]
	>([]);
	const [isLoadingFactors, setIsLoadingFactors] = useState(false);

	// Signals State
	const [signals, setSignals] = useState<Signal[]>([]);
	const [isLoadingSignals, setIsLoadingSignals] = useState(false);

	// Future State
	const [futureState, setFutureState] = useState("");
	const [isLoadingFuture, setIsLoadingFuture] = useState(false);

	// Corrective Actions State
	const [correctiveActions, setCorrectiveActions] = useState<
		CorrectiveAction[]
	>([]);
	const [isLoadingActions, setIsLoadingActions] = useState(false);

	// Linking State
	const [actionLinks, setActionLinks] = useState<ActionLink[]>([]);
	const [currentLinkingIndex, setCurrentLinkingIndex] = useState(0);
	const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
		{},
	);

	// Reminder dialog for current reflection text
	const [showReminder, setShowReminder] = useState(false);

	// Notification/Toast state
	const [notification, setNotification] = useState<{
		message: string;
		type: "success" | "error" | "info";
	} | null>(null);
	const notificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Use reflection persistence hook for saving and completing reflections
	const { saveReflectionDraft, completeReflection } =
		useReflectionPersistenceExtended({
			user,
			currentReflectionId,
			reflectionIdRef,
			setCurrentReflectionId,
			currentStepRef,
			setIsSaving,
			setLastSaved,
			setIsDraft,
			selectedCategory,
			reflectionTitle,
			reflectionDescription,
			futureState,
			qaHistory,
			currentQuestion,
			currentAnswer,
			conversationHistory,
			selectedTheme,
			contributingFactors,
			signals,
			correctiveActions,
			topicAssignmentId,
			assignedTopic,
			draftId,
			saveInProgressRef,
			saveMutexRef,
		});

	// Show notification helper
	const showNotification = (
		message: string,
		type: "success" | "error" | "info" = "success",
		duration: number = 3000,
	) => {
		// Clear any existing timeout
		if (notificationTimeoutRef.current) {
			clearTimeout(notificationTimeoutRef.current);
		}

		setNotification({ message, type });

		// Auto-hide after duration
		notificationTimeoutRef.current = setTimeout(() => {
			setNotification(null);
			notificationTimeoutRef.current = null;
		}, duration);
	};

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (notificationTimeoutRef.current) {
				clearTimeout(notificationTimeoutRef.current);
			}
		};
	}, []);

	// Suggested Reflection State
	const [suggestedReflection, setSuggestedReflection] =
		useState<SuggestedReflection | null>(null);
	const [loadingSuggestion, setLoadingSuggestion] = useState(false);

	/**
	 * Save links for a specific corrective action to the database
	 */
	const handleSaveActionLinks = async (actionId: string) => {
		if (!user?.id || !currentReflectionId) {
			debugWarn("Cannot save action links - no user or reflection ID");
			return;
		}

		try {
			// Find the action link data for this action
			const actionLink = actionLinks.find(
				(link) => link.correctiveActionId === actionId,
			);
			if (!actionLink) {
				debugWarn("No action link found for action ID:", actionId);
				return;
			}

			// Get the database IDs for the corrective action from state
			// Since we're working with the in-memory actions that have local IDs,
			// we need to find the corresponding database corrective action
			const action = correctiveActions.find((a) => a.id === actionId);
			if (!action) {
				debugWarn("Corrective action not found in state:", actionId);
				return;
			}

			// Query database to get the actual corrective action by title and reflection_id
			const { data: dbAction, error: actionError } = await supabase
				.from("corrective_actions")
				.select("id")
				.eq("reflection_id", currentReflectionId)
				.eq("id", action.id)
				.maybeSingle();

			if (actionError || !dbAction) {
				debugWarn("Database corrective action not found:", actionError);
				return;
			}

			// Use the linked factor IDs directly (they should already be database UUIDs)
			const factorDbIds = actionLink.linkedFactors;

			// Use the linked signal IDs directly (they should already be database UUIDs)
			const signalDbIds = actionLink.linkedSignals;

			// Save the links using the service
			const result = await saveActionLinksService(
				dbAction.id,
				factorDbIds,
				signalDbIds,
				user.id,
			);

			if (result.error) {
				debugError("Error saving action links:", result.error);
			} else {
				debugLog(
					"✅ Action links saved successfully for action:",
					action.title,
				);
			}
		} catch (error) {
			debugError("Error in handleSaveActionLinks:", error);
		}
	};

	// Load assigned topic from URL parameters
	useEffect(() => {
		const loadAssignedTopic = async () => {
			const topicId = searchParams.get("topic");
			const assignmentId = searchParams.get("assignment");

			if (!topicId || !user) return;

			try {
				const { data: topic, error } = await supabase
					.from("topics")
					.select("*")
					.eq("id", topicId)
					.single();

				if (error) {
					console.error("Error loading topic:", error);
					return;
				}

				setAssignedTopic({
					topic_id: topic.id,
					title: topic.name,
					description: topic.description,
					initial_question: topic.initial_question,
					assignment_id: assignmentId || "",
				});

				setTopicAssignmentId(assignmentId);

				// Build comprehensive theme prompt for AI
				// Use prompt_creation (Conversation Guide) as primary guidance if available
				// Otherwise, build from available fields
				let themePrompt = `Assigned topic: ${topic.name}`;

				if (topic.description) {
					themePrompt += `\n\nDescription: ${topic.description}`;
				}

				if (topic.initial_question) {
					themePrompt += `\n\nOpening Question: ${topic.initial_question}`;
				}

				if (topic.prompt_creation) {
					themePrompt += `\n\nConversation Guide:\n${topic.prompt_creation}`;
				}

				// Always set the theme so AI has context, even if minimal
				setSelectedTheme({
					id: topic.id,
					name: topic.name,
					description: topic.description,
					prompt: themePrompt,
					is_default: false,
					sort_order: 0,
				});

				// Set the topic ID as the category (for proper tagging)
				setSelectedCategory(topic.topic_id);
			} catch (error) {
				console.error("Error loading assigned topic:", error);
			}
		};

		loadAssignedTopic();
	}, [searchParams, user]);

	// Load reflection themes from database
	useEffect(() => {
		const fetchReflectionThemes = async () => {
			try {
				const { data, error } = await supabase
					.from("reflection_themes")
					.select("*")
					.order("sort_order");

				if (error) {
					if (
						error.message?.includes(
							'relation "public.reflection_themes" does not exist',
						)
					) {
						console.warn(
							"reflection_themes table not found. Please run add-reflection-themes-table.sql in Supabase SQL Editor",
						);
						setReflectionThemes([]);
						return;
					}
					console.error("Error fetching reflection themes:", error);
					return;
				}

				setReflectionThemes(data || []);
			} catch (error) {
				console.error("Error loading reflection themes:", error);
				console.error("Error details:", JSON.stringify(error, null, 2));
				if (error instanceof Error) {
					console.error("Error message:", error.message);
				}
			}
		};

		fetchReflectionThemes();
	}, []);

	// Handle AI-suggested reflection from daily check-ins
	useEffect(() => {
		const loadSuggestedReflection = async () => {
			const isSuggested = searchParams.get("suggested") === "true";
			const isFromCheckinsTab = searchParams.get("tab") === "checkins";
			const suggestedTitle = searchParams.get("title");
			const suggestedDescription = searchParams.get("description");
			const suggestedReasoning = searchParams.get("reasoning");

			// Only handle suggested bootstrapping while on intro or questions steps to avoid step resets later
			if (!(currentStep === "intro" || currentStep === "questions")) {
				return;
			}

			// Check if this is a suggested reflection (either explicit or from checkins tab with all parameters)
			const hasSuggestionData =
				suggestedTitle && suggestedDescription && suggestedReasoning;
			const isSuggestedReflection =
				(isSuggested || isFromCheckinsTab) && hasSuggestionData;

			if (!isSuggestedReflection || !suggestedTitle || !user) return;

			debugLog("🎯 Loading AI-suggested reflection with enhanced prompt:", {
				title: suggestedTitle,
				description: suggestedDescription,
				reasoning: suggestedReasoning,
				isSuggested,
				isFromCheckinsTab,
				hasSuggestionData,
			});

			// Set the suggested topic data - try to find topic ID by title
			try {
				const { data: topicData, error: topicError } = await supabase
					.from("topics")
					.select("id")
					.eq("name", suggestedTitle)
					.maybeSingle();

				if (!topicError && topicData) {
					console.log(
						`✅ Found topic ID for AI suggestion: ${suggestedTitle} -> ${topicData.id}`,
					);
					setSelectedCategory(topicData.id);
				} else {
					setSelectedCategory(suggestedTitle);
				}
			} catch (error) {
				setSelectedCategory(suggestedTitle);
			}
			setReflectionTitle(suggestedTitle);

			// Create a theme prompt for AI to use the suggestion context
			const themePrompt = `FOCUSED REFLECTION TOPIC: ${suggestedTitle}

TOPIC CONTEXT: ${suggestedDescription}

ANALYSIS FROM DAILY CHECK-INS: ${suggestedReasoning}

CRITICAL INSTRUCTIONS:
- This reflection was AI-suggested based on the user's daily check-in patterns
- The user ALREADY KNOWS this topic was suggested for them - DO NOT ask them to explain or introduce the topic
- DO NOT ask "Can you tell me about [topic]?" or "What does [topic] mean to you?"
- ASSUME the context is already established and dive directly into specific, probing questions
- Start immediately with deep, insightful questions about their personal experience with this topic

Focus your questions on helping the user examine:
- Specific recent situations where this topic affected them
- The triggers and patterns they've noticed
- Their emotional and physical responses
- How this connects to other areas of their life
- What they've tried before and what worked/didn't work
- Concrete next steps they could take

Keep every question laser-focused on this suggested topic with specificity and depth.`;

			debugLog("🎯 Enhanced theme prompt created for AI deep dive:");
			debugLog("📋 Prompt length:", themePrompt.length);
			debugLog(
				"🎯 Contains focus instructions:",
				themePrompt.includes("CRITICAL INSTRUCTIONS"),
			);
			console.log(
				"🚫 Contains no-explanation directive:",
				themePrompt.includes("DO NOT ask"),
			);

			const suggestedTheme = {
				id: "suggested",
				name: suggestedTitle,
				description: suggestedDescription,
				prompt: themePrompt,
				is_default: false,
				sort_order: 0,
			};

			setSelectedTheme(suggestedTheme);

			// Skip intro and go directly to questions (AI will generate the first question)
			setCurrentStep("questions");

			// Automatically start the AI conversation for suggested reflections (deterministic)
			debugLog("🎯 Auto-starting AI conversation for suggested reflection");
			if (!hasAutoStartedQuestionsRef.current) {
				hasAutoStartedQuestionsRef.current = true;
				// Pass theme directly to avoid React state timing issues
				beginQuestions(suggestedTheme);
			}
		};

		loadSuggestedReflection();
	}, [searchParams, user]);

	// Load specific draft reflection if ID is provided
	useEffect(() => {
		const loadSpecificDraft = async () => {
			if (!user) {
				// No user available for reflection
				return;
			}

			if (!draftId) {
				// Starting new reflection (no draftId)
				return;
			}

			// Skip loading during AI summary transition to prevent step reset
			if (isTransitioningFromAIRef.current) {
				console.log("Skipping draft load during AI summary transition");
				return;
			}

			// Loading specific draft reflection

			try {
				// First try to load with is_draft filter
				let { data: draft, error } = await supabase
					.from("reflections")
					.select("*")
					.eq("id", draftId)
					.eq("user_id", user.id)
					.eq("is_draft", true)
					.single();

				// Handle different error cases
				if (error) {
					if (error.code === "42703" || error.message?.includes("is_draft")) {
						// is_draft column doesn't exist, try without it
						console.warn(
							"is_draft column not found, trying without draft filter",
						);

						const { data: fallbackDraft, error: fallbackError } = await supabase
							.from("reflections")
							.select("*")
							.eq("id", draftId)
							.eq("user_id", user.id)
							.single();

						if (fallbackError) {
							if (fallbackError.code === "PGRST116") {
								console.warn("Draft reflection not found:", draftId);
								alert(
									"The draft reflection you're trying to access was not found. Starting a new reflection instead.",
								);
								// Redirect to new reflection since draft doesn't exist
								navigate("/reflection/new");
								return;
							}
							console.error(
								"Error loading draft (fallback):",
								fallbackError.message || fallbackError,
							);
							return;
						}

						draft = fallbackDraft;
						error = null;
					} else if (error.code === "PGRST116") {
						// No rows found - draft doesn't exist or isn't marked as draft
						console.warn(
							"Draft reflection not found or not marked as draft:",
							draftId,
						);

						// Try to load as a regular reflection (maybe it was completed)
						const { data: completedReflection, error: completedError } =
							await supabase
								.from("reflections")
								.select("*")
								.eq("id", draftId)
								.eq("user_id", user.id)
								.single();

						if (completedError) {
							if (completedError.code === "PGRST116") {
								console.warn("Reflection not found at all:", draftId);
								alert(
									"The reflection you're trying to access was not found. Starting a new reflection instead.",
								);
								// Redirect to new reflection since it doesn't exist
								navigate("/reflection/new");
								return;
							}
							console.error(
								"Error loading completed reflection:",
								completedError.message || completedError,
							);
							return;
						}

						// Found the reflection but it's completed, redirect to view page
						console.log(
							"Reflection is completed, redirecting to view page:",
							completedReflection.id,
						);
						alert(
							"This reflection has already been completed. Redirecting to the view page.",
						);
						navigate(`/reflection/${completedReflection.id}`);
						return;
					} else {
						console.error(
							"Error loading specific draft:",
							error.message || error,
						);
						console.error("Full error object:", JSON.stringify(error, null, 2));
						return;
					}
				}

				if (draft) {
					console.log("📖 Loading draft reflection:", {
						id: draft.id,
						title: draft.title,
						description: draft.description
							? draft.description.substring(0, 100) + "..."
							: "[EMPTY]",
						has_title: !!draft.title,
						has_description: !!draft.description,
					});

					setCurrentReflectionId(draft.id);
					reflectionIdRef.current = draft.id;
					setReflectionTitle(draft.title || "");
					setReflectionDescription(draft.description || "");
					setFutureState(draft.future_state || "");

					console.log("✅ Set title state to:", draft.title || "[EMPTY]");
					console.log(
						"✅ Set description state to:",
						draft.description
							? draft.description.substring(0, 50) + "..."
							: "[EMPTY]",
					);

					const forceFreshParam = searchParams.get("fresh") === "1";
					const forceFreshFlag = ((): boolean => {
						try {
							return sessionStorage.getItem("reflectionForceFresh") === "1";
						} catch {
							return false;
						}
					})();
					const isFresh = forceFreshParam || forceFreshFlag;
					// Don't remove the flag immediately - keep it until we're sure we're past the initial navigation
					// The back button detection logic also checks this flag, so we need to keep it for a bit
					// Load Q&A data from qa_results unless this is a freshly created reflection
					if (!isFresh && draft.qa_results) {
						try {
							const qaData =
								typeof draft.qa_results === "string"
									? JSON.parse(draft.qa_results)
									: draft.qa_results;

							setQaHistory(qaData.history || []);
							setCurrentQuestion(qaData.currentQuestion || "");
							setCurrentAnswer(qaData.currentAnswer || "");
							setConversationHistory(qaData.conversationHistory || []);
							setSelectedCategory(qaData.selectedCategory || null);
							setSelectedTheme(qaData.selectedTheme || null); // Restore theme
							// Prefer explicit step in URL when continuing to a draft, fallback to stored qa_results, then to instructions
							const stepFromUrl = searchParams.get("step") as ReflectionStep;
							const loadedStep =
								(stepFromUrl as ReflectionStep) ||
								qaData.currentStep ||
								"instructions";
							console.log(
								"Loaded draft qa_results.currentStep:",
								qaData.currentStep,
								" - stepFromUrl:",
								stepFromUrl,
								" - using:",
								loadedStep,
							);
							setCurrentStep(loadedStep);
							// Update URL to match loaded step from draft
							if (loadedStep && loadedStep !== "intro") {
								const newSearchParams = new URLSearchParams(
									searchParams.toString(),
								);
								newSearchParams.set("step", loadedStep);
								setSearchParams(newSearchParams, { replace: true });
							}
						} catch (parseError) {
							console.warn("Error parsing qa_results:", parseError);
							const stepFromUrl = searchParams.get("step") as ReflectionStep;
							const fallbackStep: ReflectionStep =
								stepFromUrl && VALID_STEPS.includes(stepFromUrl)
									? stepFromUrl
									: "intro";
							setCurrentStep(fallbackStep);
							const newSearchParams = new URLSearchParams(
								searchParams.toString(),
							);
							newSearchParams.set("step", fallbackStep);
							setSearchParams(newSearchParams, { replace: true });
						}
					} else {
						// If this is a new fresh reflection, ensure QA state is clean
						const isFresh = searchParams.get("fresh") === "1";
						if (isFresh) {
							setQaHistory([]);
							setCurrentQuestion("");
							setCurrentAnswer("");
							setConversationHistory([]);
						}

						// Determine step based on draft data if no qa_results
						// Infer step from what data exists in the draft
						let inferredStep: ReflectionStep = "intro";
						if (draft.future_state) {
							inferredStep = "future-state";
						} else if (draft.description) {
							inferredStep = "description";
						} else if (draft.title) {
							inferredStep = "questions"; // If they have a title, they've likely started questions
						}

						const stepFromUrl = searchParams.get("step") as ReflectionStep;
						// Prefer URL step, then inferred step, then intro
						const fallbackStep: ReflectionStep =
							stepFromUrl && VALID_STEPS.includes(stepFromUrl)
								? stepFromUrl
								: VALID_STEPS.includes(inferredStep)
									? inferredStep
									: "intro";

						console.log(
							"Draft has no qa_results, inferred step:",
							inferredStep,
							"using:",
							fallbackStep,
						);
						setCurrentStep(fallbackStep);
						const newSearchParams = new URLSearchParams(
							searchParams.toString(),
						);
						newSearchParams.set("step", fallbackStep);
						setSearchParams(newSearchParams, { replace: true });
					}

					// Load related data
					try {
						await loadContributingFactors(draft.id);
						await loadSignals(draft.id);
						await loadCorrectiveActions(draft.id);
						await loadActionLinksFromDatabase(draft.id);
					} catch (loadError) {
						console.warn("Error loading related data:", loadError);
					}

					setIsDraft(true);
				}
			} catch (error) {
				console.error("Error loading specific draft reflection:", error);
				console.error("Error details:", {
					message: error instanceof Error ? error.message : "Unknown error",
					stack: error instanceof Error ? error.stack : "No stack trace",
					draftId,
					userId: user?.id,
				});
			}
		};

		loadSpecificDraft();
	}, [draftId, user]);

	const hasCheckedDraftForStepRef = useRef<string | null>(null);

	// Check for existing drafts when accessing new reflection with step parameter
	useEffect(() => {
		const checkAndRedirectToDraft = async () => {
			const stepFromUrl = searchParams.get("step") as ReflectionStep;

			// This check is only intended for the "new reflection" entry route.
			if (location.pathname !== "/reflection/new") {
				return;
			}

			// Only check for drafts if:
			// 1. We have a step parameter in URL
			// 2. We're on the "new" route (no draftId)
			// 3. User is available
			if (
				stepFromUrl &&
				VALID_STEPS.includes(stepFromUrl) &&
				!draftId &&
				user
			) {
				// Avoid running this check repeatedly for the same step on the same /reflection/new path
				if (
					location.pathname === "/reflection/new" &&
					hasCheckedDraftForStepRef.current === stepFromUrl
				) {
					console.log("Skipping repeated draft check for step:", stepFromUrl);
					return;
				}

				console.log("Checking for existing draft for step:", stepFromUrl);
				// Mark that we've checked this step on /reflection/new to avoid repeats
				if (location.pathname === "/reflection/new") {
					hasCheckedDraftForStepRef.current = stepFromUrl;
				}

				try {
					// Check if there's an existing draft
					const { data: drafts, error } = await supabase
						.from("reflections")
						.select("id, title, description, qa_results")
						.eq("user_id", user.id)
						.eq("is_draft", true)
						.order("updated_at", { ascending: false })
						.limit(1);

					if (error) {
						// If is_draft column doesn't exist, just proceed with normal step restoration
						if (error.message?.includes("is_draft") || error.code === "42703") {
							console.log("No draft support, proceeding with step restoration");
							setIsRestoringFromUrl(true);
							setCurrentStep(stepFromUrl);
							setIsRestoringFromUrl(false);
							return;
						}
						throw error;
					}

					if (drafts && drafts.length > 0) {
						const draft = drafts[0];
						console.log(
							"Found existing draft, redirecting to continue flow:",
							draft.id,
						);
						// Redirect to continue flow with the step parameter
						navigate(`/reflection/continue/${draft.id}?step=${stepFromUrl}`, {
							replace: true,
						});
						return;
					}

					// No existing draft, proceed with normal step restoration
					console.log(
						"No existing draft found, starting new reflection at step:",
						stepFromUrl,
					);
					setIsRestoringFromUrl(true);
					setCurrentStep(stepFromUrl);
					setIsRestoringFromUrl(false);
				} catch (error) {
					console.error("Error checking for existing drafts:", error);
					// Fallback to normal step restoration
					setIsRestoringFromUrl(true);
					setCurrentStep(stepFromUrl);
					setIsRestoringFromUrl(false);
				}
			}
		};

		checkAndRedirectToDraft();
	}, [searchParams, draftId, user, navigate]);

	// Restore step from URL on page load (for continue flow)
	useEffect(() => {
		const stepFromUrl = searchParams.get("step") as ReflectionStep;

		// Only restore from URL if we have a draftId (continue flow) and no data loaded yet
		if (
			stepFromUrl &&
			VALID_STEPS.includes(stepFromUrl) &&
			draftId &&
			!currentReflectionId
		) {
			console.log("Restoring step from URL for continue flow:", stepFromUrl);
			setIsRestoringFromUrl(true);
			setCurrentStep(stepFromUrl);
			setIsRestoringFromUrl(false);
		}
	}, [searchParams, draftId, currentReflectionId]);

	// Handle browser back button - navigate to reflections page when step param is removed
	useEffect(() => {
		// If we're on the questions step and the URL no longer has the step param,
		// the user clicked back - navigate to reflections page instead of staying in the flow
		// Don't trigger if:
		// - This is a fresh reflection (has fresh=1 param)
		// - We're restoring from URL
		// - We don't have a reflection ID yet (still creating)
		// - The step param is being set (check if it exists in URL or was just set)
		const isFreshReflection = searchParams.get("fresh") === "1";
		const hasReflectionId = currentReflectionId || draftId;
		const hasStepParam = searchParams.has("step");
		// Also check sessionStorage for the fresh flag as a fallback
		const hasFreshFlag = (() => {
			try {
				return sessionStorage.getItem("reflectionForceFresh") === "1";
			} catch {
				return false;
			}
		})();

		if (
			currentStep === "questions" &&
			!hasStepParam &&
			!isRestoringFromUrl &&
			!isNavigatingBackRef.current &&
			!isFreshReflection &&
			!hasFreshFlag && // Also check sessionStorage flag
			hasReflectionId // Only navigate back if we have a valid reflection (not during creation)
		) {
			console.log(
				"Back button detected (no step param), navigating to /reflections",
			);
			isNavigatingBackRef.current = true;
			navigate("/reflections");
			// Reset flag after navigation
			setTimeout(() => {
				isNavigatingBackRef.current = false;
			}, 100);
		}
	}, [
		searchParams,
		currentStep,
		isRestoringFromUrl,
		navigate,
		currentReflectionId,
		draftId,
	]);

	// Update URL when step changes (but not during URL restoration or draft loading)
	useEffect(() => {
		if (
			!isRestoringFromUrl &&
			currentStep !== "intro" &&
			!isNavigatingBackRef.current
		) {
			const newSearchParams = new URLSearchParams(searchParams.toString());
			newSearchParams.set("step", currentStep);

			// Don't navigate if we're already on the correct step
			if (searchParams.get("step") !== currentStep) {
				console.log("Updating URL with step:", currentStep);
				// Use replace for subsequent step changes to avoid cluttering history,
				// but allow the first step (questions) to create a history entry
				const isFirstStep =
					!searchParams.has("step") && currentStep === "questions";
				setSearchParams(newSearchParams, { replace: !isFirstStep });
			}
		} else if (currentStep === "intro") {
			// Remove step parameter when back at intro
			const newSearchParams = new URLSearchParams(searchParams.toString());
			if (newSearchParams.has("step")) {
				newSearchParams.delete("step");
				setSearchParams(newSearchParams, { replace: true });
			}
		}
	}, [currentStep, isRestoringFromUrl, searchParams, setSearchParams]);

	// Auto-saves completely removed: saves only occur when:
	// 1. User explicitly clicks Next/Save/OK buttons
	// 2. AI returns current_state or future_state text
	// No timer-based or background saves.

	// Fetch suggested reflection from analysis
	useEffect(() => {
		const fetchSuggestedReflection = async () => {
			if (!user || assignedTopic) return; // Don't show suggestion if there's an assigned topic

			setLoadingSuggestion(true);
			try {
				// Fixed date calculation with validation
				const now = new Date();
				const currentYear = now.getFullYear();

				// Validate that we're in a reasonable year range
				if (currentYear < 2020 || currentYear > 2030) {
					console.warn("🚨 ReflectionFlow: Invalid system date detected:", {
						year: currentYear,
						fullDate: now.toISOString(),
					});
					setLoadingSuggestion(false);
					return;
				}

				const today = now.toISOString().split("T")[0];

				let analysisData = null;
				let error = null;

				try {
					const result = await supabase
						.from("daily_checkin_analysis")
						.select("analysis_data")
						.eq("user_id", user.id)
						.eq("analysis_date", today)
						.maybeSingle(); // Use maybeSingle to avoid 406 errors

					analysisData = result.data;
					error = result.error;
				} catch (fetchError: any) {
					console.log(
						"🚨 ReflectionFlow: Database fetch error for suggestion:",
						fetchError,
					);
					// Handle specific 406 errors gracefully
					if (
						(fetchError as any)?.message?.includes("406") ||
						(fetchError as any)?.message?.includes("Not Acceptable")
					) {
						console.warn(
							"🚨 ReflectionFlow: 406 error detected - skipping suggested reflection",
						);
						setLoadingSuggestion(false);
						return;
					}
					error = fetchError as any;
				}

				if (
					analysisData &&
					!error &&
					analysisData.analysis_data?.reflectionSuggestion
				) {
					setSuggestedReflection(
						analysisData.analysis_data.reflectionSuggestion,
					);
				}
			} catch (error) {
				console.log("No suggested reflection available:", error);
			} finally {
				setLoadingSuggestion(false);
			}
		};

		fetchSuggestedReflection();
	}, [user, assignedTopic]);

	const loadDraftReflection = async () => {
		if (!user) return;

		try {
			// Set isRestoringFromUrl to prevent auto-actions during draft load
			setIsRestoringFromUrl(true);

			// First, try to check if is_draft column exists by querying without it
			const { data: allReflections, error: checkError } = await supabase
				.from("reflections")
				.select("id")
				.eq("user_id", user.id)
				.limit(1);

			if (checkError) {
				console.error("Database access error:", checkError.message, checkError);
				setIsRestoringFromUrl(false);
				return false;
			}

			// Try to look for existing draft reflections
			const { data: drafts, error } = await supabase
				.from("reflections")
				.select("*")
				.eq("user_id", user.id)
				.eq("is_draft", true)
				.order("updated_at", { ascending: false })
				.limit(1);

			if (error) {
				console.error(
					"Error querying drafts:",
					error.message,
					error.code,
					error,
				);
				// If is_draft column doesn't exist, just return false
				if (error.message?.includes("is_draft") || error.code === "42703") {
					console.warn(
						"is_draft column doesn't exist. Please run add-draft-support.sql",
					);
					setIsRestoringFromUrl(false);
					return false;
				}
				setIsRestoringFromUrl(false);
				throw error;
			}

			if (drafts && drafts.length > 0) {
				const draft = drafts[0];
				console.log("Loading draft reflection:", draft);

				setCurrentReflectionId(draft.id);
				reflectionIdRef.current = draft.id;
				setReflectionTitle(draft.title || "");
				setReflectionDescription(draft.description || "");
				setFutureState(draft.future_state || "");

				// Load Q&A data from qa_results with better error handling
				if (draft.qa_results) {
					try {
						const qaData =
							typeof draft.qa_results === "string"
								? JSON.parse(draft.qa_results)
								: draft.qa_results;

						setQaHistory(qaData.history || []);
						setCurrentQuestion(qaData.currentQuestion || "");
						setCurrentAnswer(qaData.currentAnswer || "");
						setConversationHistory(qaData.conversationHistory || []);
						setSelectedCategory(qaData.selectedCategory || null);

						// Prioritize URL step parameter over database-saved step
						const urlStep = searchParams.get("step") as ReflectionStep;
						const stepToUse = urlStep || qaData.currentStep || "intro";
						console.log("📍 loadDraftReflection - setting step:", {
							urlStep,
							dbStep: qaData.currentStep,
							finalStep: stepToUse,
						});
						setCurrentStep(stepToUse);

						// Restore AI-generated items that weren't selected yet
						const mergeUniqueById = (prev: any[], incoming: any[]) => {
							const map = new Map<string, any>();
							// add previous items first
							prev.forEach((p) => {
								if (p && p.id) map.set(p.id, p);
							});
							// add/overwrite with incoming items
							incoming.forEach((i) => {
								if (i && i.id) map.set(i.id, { ...map.get(i.id), ...i });
							});
							return Array.from(map.values());
						};

						if (
							qaData.aiGeneratedFactors &&
							qaData.aiGeneratedFactors.length > 0
						) {
							console.log(
								"Restoring AI-generated factors:",
								qaData.aiGeneratedFactors.length,
							);
							setContributingFactors((prev) => {
								// only merge unique by id to avoid duplicates on reload
								return mergeUniqueById(prev, qaData.aiGeneratedFactors);
							});
						}
						if (
							qaData.aiGeneratedSignals &&
							qaData.aiGeneratedSignals.length > 0
						) {
							console.log(
								"Restoring AI-generated signals:",
								qaData.aiGeneratedSignals.length,
							);
							setSignals((prev) => {
								return mergeUniqueById(prev, qaData.aiGeneratedSignals);
							});
						}
						if (
							qaData.aiGeneratedActions &&
							qaData.aiGeneratedActions.length > 0
						) {
							console.log(
								"Restoring AI-generated actions:",
								qaData.aiGeneratedActions.length,
							);
							setCorrectiveActions((prev) => {
								return mergeUniqueById(prev, qaData.aiGeneratedActions);
							});
						}
					} catch (parseError) {
						console.warn("Error parsing qa_results:", parseError);
					}
				}

				// Load contributing factors
				try {
					await loadContributingFactors(draft.id);
				} catch (factorError) {
					console.warn("Error loading contributing factors:", factorError);
				}

				// Load signals
				try {
					await loadSignals(draft.id);
				} catch (signalError) {
					console.warn("Error loading signals:", signalError);
				}

				// Load corrective actions
				try {
					await loadCorrectiveActions(draft.id);
				} catch (actionError) {
					const errorMessage =
						actionError instanceof Error
							? actionError.message
							: "Failed to load corrective actions";
					console.warn(
						"Error loading corrective actions:",
						errorMessage,
						actionError,
					);
				}

				// Load action links
				try {
					await loadActionLinksFromDatabase(draft.id);
				} catch (linkError) {
					console.warn("Error loading action links:", linkError);
				}

				setIsDraft(true);
				setIsRestoringFromUrl(false); // Draft loading complete
				return true; // Found and loaded draft
			}

			// No drafts found
			setIsRestoringFromUrl(false);
		} catch (error: any) {
			const msg = error instanceof Error ? error.message : String(error);
			console.error("Error loading draft reflection:", msg, error);
			setIsRestoringFromUrl(false);
		}
		return false; // No draft found
	};

	// Load draft reflection when draftId is present
	useEffect(() => {
		if (draftId && user && !currentReflectionId) {
			console.log("Loading draft reflection:", draftId);
			loadDraftReflection().catch((error) => {
				console.error("Error loading draft reflection:", error);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [draftId, user, currentReflectionId]);

	const loadContributingFactors = async (reflectionId: string) => {
		try {
			const { data, error } = await supabase
				.from("contributing_factors")
				.select("*")
				.eq("reflection_id", reflectionId);

			if (error) throw error;

			if (data) {
				const loadedFactors = data.map((factor) => ({
					id: factor.id,
					title: factor.title,
					description: factor.description,
					selected: true, // Assume selected since they were saved
				}));
				// Append to existing factors (don't replace AI-generated ones)
				setContributingFactors((prev) => {
					const map = new Map<string, any>();
					prev.forEach((p) => {
						if (p && p.id) map.set(p.id, p);
					});
					loadedFactors.forEach((f) => {
						if (f && f.id) map.set(f.id, f);
					});
					return Array.from(map.values());
				});
			}
		} catch (error) {
			console.error("Error loading contributing factors:", error);
		}
	};

	const loadSignals = async (reflectionId: string) => {
		try {
			const { data, error } = await supabase
				.from("signals")
				.select("*")
				.eq("reflection_id", reflectionId);

			if (error) throw error;

			if (data) {
				const loadedSignals = data.map((signal) => ({
					id: signal.id,
					title: signal.title,
					description: signal.description,
					selected: true, // Assume selected since they were saved
				}));
				// Append to existing signals (don't replace AI-generated ones)
				setSignals((prev) => {
					const map = new Map<string, any>();
					prev.forEach((p) => {
						if (p && p.id) map.set(p.id, p);
					});
					loadedSignals.forEach((s) => {
						if (s && s.id) map.set(s.id, s);
					});
					return Array.from(map.values());
				});
			}
		} catch (error) {
			console.error("Error loading signals:", error);
		}
	};

	const loadCorrectiveActions = async (reflectionId: string) => {
		try {
			const { data, error } = await supabase
				.from("corrective_actions")
				.select("*")
				.eq("reflection_id", reflectionId);

			if (error) throw error;

			if (data) {
				const loadedActions = data.map((action) => ({
					id: action.id,
					title: action.title,
					description: action.description,
					selected: true, // Assume selected since they were saved
				}));
				// Append to existing actions (don't replace AI-generated ones)
				setCorrectiveActions((prev) => {
					const map = new Map<string, any>();
					prev.forEach((p) => {
						if (p && p.id) map.set(p.id, p);
					});
					loadedActions.forEach((a) => {
						if (a && a.id) map.set(a.id, a);
					});
					return Array.from(map.values());
				});
			}
		} catch (error) {
			const errorMessage =
				error instanceof Error
					? error.message
					: "An unexpected error occurred while loading corrective actions";
			console.error("Error loading corrective actions:", errorMessage, error);
		}
	};

	const loadActionLinksFromDatabase = async (reflectionId: string) => {
		if (!user?.id) {
			debugWarn("Cannot load action links - no user");
			return;
		}

		try {
			const result = await loadActionLinksService(reflectionId, user.id);

			if (result.error) {
				debugWarn("Error loading action links:", result.error);
				return;
			}

			if (result.links && result.links.length > 0) {
				debugLog(
					"✅ Loaded action links from database:",
					result.links.length,
					"links",
				);
				setActionLinks(result.links);
			}
		} catch (error) {
			debugError("Error in loadActionLinksFromDatabase:", error);
		}
	};

	const checkForDrafts = async () => {
		if (!user) return false;

		try {
			const { data: drafts, error } = await supabase
				.from("reflections")
				.select("id")
				.eq("user_id", user.id)
				.eq("is_draft", true)
				.limit(1);

			if (error) {
				// If is_draft column doesn't exist, just return false
				if (error.message?.includes("is_draft") || error.code === "42703") {
					console.warn(
						"is_draft column doesn't exist. Please run add-draft-support.sql",
					);
					return false;
				}
				console.error("Error checking for drafts:", error);
				return false;
			}

			return drafts && drafts.length > 0;
		} catch (error) {
			console.error("Error checking for drafts:", error);
			return false;
		}
	};

	const startReflection = async (
		category?: string,
		theme?: ReflectionTheme,
	) => {
		console.log(
			"startReflection called with category:",
			category,
			"theme:",
			theme,
		);
		// If we're continuing a specific draft (draftId is present), skip draft loading
		if (draftId) {
			// Draft is already loaded by useEffect, just proceed
			setSelectedCategory(category || null);
			return;
		}

		// Start fresh reflection - need to create a new draft first
		setSelectedCategory(category || null);
		if (theme) {
			setSelectedTheme(theme);
		}

		// Clear any persisted QA drafts and session cache to avoid leaking old data
		try {
			sessionStorage.setItem("reflectionForceFresh", "1");
			sessionStorage.removeItem("reflectionSessionData");
			const keys = Object.keys(localStorage);
			for (const k of keys) {
				if (k === "qa_draft_new" || k.startsWith("qa_draft_")) {
					localStorage.removeItem(k);
				}
			}
		} catch {}

		// Reset state
		reflectionIdRef.current = null;
		setCurrentReflectionId(null);
		setQaHistory([]);
		setCurrentQuestion("");
		setCurrentAnswer("");
		setConversationHistory([]);
		setReflectionTitle("");
		setReflectionDescription("");
		setFutureState("");
		setContributingFactors([]);
		setSignals([]);
		setCorrectiveActions([]);
		setActionLinks([]);
		setIsDraft(false);
		hasAutoStartedQuestionsRef.current = false;
		hasCheckedDraftForStepRef.current = null;

		// Create a brand-new draft reflection immediately
		try {
			const baseData: any = {
				user_id: user?.id,
				title: "Untitled Reflection",
				description: "",
				current_state: "",
				future_state: "",
				qa_results: null,
				topic_tags: [],
			};
			let newId: string | null = null;
			// Try with is_draft
			const withDraft = await supabase
				.from("reflections")
				.insert({ ...baseData, is_draft: true })
				.select("id")
				.single();
			if (
				withDraft.error &&
				(withDraft.error.code === "42703" ||
					String(withDraft.error.message || "").includes("is_draft"))
			) {
				const withoutDraft = await supabase
					.from("reflections")
					.insert(baseData)
					.select("id")
					.single();
				if (withoutDraft.error) throw withoutDraft.error;
				newId = withoutDraft.data?.id || null;
			} else {
				if (withDraft.error) throw withDraft.error;
				newId = withDraft.data?.id || null;
			}
			if (newId) {
				reflectionIdRef.current = newId;
				setCurrentReflectionId(newId);
				setIsDraft(true);
				// Set step immediately to prevent back button detection from triggering
				setCurrentStep("questions");
				const params = new URLSearchParams();
				params.set("step", "questions");
				params.set("fresh", "1");
				// Navigate to the new reflection
				navigate(`/reflection/continue/${newId}?${params.toString()}`);

				// Don't call beginQuestions here - let the component's useEffect hooks
				// handle starting questions when it detects we're on the questions step
				// with a fresh reflection. This prevents race conditions.
			} else {
				console.error("Failed to create new reflection: no ID returned");
			}
		} catch (e) {
			console.error("Failed to create new reflection record:", e);
			return;
		}
	};

	const handleContinueDraft = (draftId: string) => {
		console.log("handleContinueDraft called with draftId:", draftId);
		if (!draftId) {
			console.error("handleContinueDraft: draftId is missing");
			return;
		}
		console.log("Navigating to:", `/reflection/continue/${draftId}`);
		// Navigate to the draft - the component will load it and restore the correct step
		navigate(`/reflection/continue/${draftId}`, { replace: false });
	};

	const beginQuestions = async (themeOverride?: ReflectionTheme) => {
		// Prevent duplicate calls
		if (beginQuestionsInProgressRef.current || isLoadingQuestion) {
			console.log(
				"beginQuestions called but already in progress/loading, skipping",
			);
			return;
		}
		beginQuestionsInProgressRef.current = true;

		setCurrentStep("questions");
		setIsLoadingQuestion(true);
		setCurrentQuestion(""); // Clear current question to show only "thinking..." during loading
		setAiError(null);

		try {
			// Check if this is an AI-recommended reflection
			const isAISuggested = searchParams.get("suggested") === "true";
			// Use themeOverride if provided (to avoid React state timing issues), otherwise fall back to selectedTheme
			const activeTheme = themeOverride || selectedTheme;
			let themePromptToSend = activeTheme?.prompt;

			if (isAISuggested) {
				// For AI-recommended reflections, construct the proper context
				const title = decodeURIComponent(searchParams.get("title") || "");
				const description = decodeURIComponent(
					searchParams.get("description") || "",
				);
				const reasoning = decodeURIComponent(
					searchParams.get("reasoning") || "",
				);

				debugLog("🎯 AI-Recommended Reflection detected:", {
					title,
					description,
					reasoning,
				});

				// Create the theme prompt in the format expected by ai-conversation
				themePromptToSend = `FOCUSED REFLECTION TOPIC: ${title}

DESCRIPTION: ${description}

AI ANALYSIS REASONING: ${reasoning}

This reflection topic was specifically identified from the user's daily check-in patterns. Focus on helping them explore their personal experience with this topic in depth.`;

				debugLog(
					"🎯 Constructed theme prompt for AI-suggested reflection:",
					themePromptToSend.substring(0, 200) + "...",
				);
				debugLog("🎯 Full theme prompt:", themePromptToSend);
			}

			console.log("���� Debug: activeTheme:", activeTheme);
			debugLog("🎯 Debug: themeOverride:", themeOverride);
			console.log("���� Debug: selectedTheme:", selectedTheme);
			debugLog("🎯 Debug: isAISuggested:", isAISuggested);
			console.log(
				"�� Debug: theme prompt being sent (preview):",
				themePromptToSend?.substring(0, 100) + "...",
			);

			if (isAISuggested) {
				debugLog(
					"🎯 ✅ AI-Recommended reflection properly configured with topic context",
				);
			}

			console.log("Calling askReflectionQuestion with:", {
				historyLength: 0,
				hasThemePrompt: !!themePromptToSend,
			});
			const response = await aiService.askReflectionQuestion(
				[],
				themePromptToSend,
			);

			console.log("Response from askReflectionQuestion:", response);

			if (response?.question) {
				console.log(
					"Setting current question to:",
					response.question.substring(0, 100) + "...",
				);
				setCurrentQuestion(response.question);
				setConversationHistory([
					{ role: "assistant", content: response.question },
				]);

				// Clear decision summary for first question
				setDecisionSummary(null);

				// Auto-save disabled for new reflections: defer saving until QA complete
				// (intentionally not calling saveReflectionDraft here)
			} else {
				console.error("Invalid response from AI service:", response);
				throw new Error("No initial question received from AI");
			}
		} catch (error) {
			console.error("ERROR getting initial question:", error);
			const errorMessage =
				error instanceof Error ? error.message : String(error);
			console.error("Setting AI error state with message:", errorMessage);
			setAiError(
				`AI conversation service is not properly configured: ${errorMessage}. Please contact support to resolve this issue.`,
			);

			setCurrentQuestion("");
			setConversationHistory([]);
		} finally {
			setIsLoadingQuestion(false);
			beginQuestionsInProgressRef.current = false;
		}
	};

	const submitAnswer = async () => {
		if (!currentAnswer.trim()) return;

		setIsLoadingQuestion(true);
		setAiError(null);

		// Add current Q&A to history
		const newQA = { question: currentQuestion, answer: currentAnswer };
		const updatedHistory = [...qaHistory, newQA];
		setQaHistory(updatedHistory);

		// Update conversation history with user response
		const updatedConversation = [
			...conversationHistory,
			{ role: "user", content: currentAnswer },
		];
		setConversationHistory(updatedConversation);

		const questionCount = updatedHistory.length;

		// APP CONTROLLED LOGIC: If we've reached max questions (and not diving deeper), show summary with options
		// OR if we've reached max dive deeper questions while diving deeper, show summary (only continue option)
		if (
			(questionCount >= REFLECTION_CONSTANTS.MAX_QUESTIONS &&
				!isDivingDeeper) ||
			(questionCount >= REFLECTION_CONSTANTS.MAX_DIVE_DEEPER_QUESTIONS &&
				isDivingDeeper)
		) {
			try {
				// Request a summary from the conversation API
				const isAISuggested = searchParams.get("suggested") === "true";
				let themePromptToSend = selectedTheme?.prompt;

				if (isAISuggested) {
					const title = decodeURIComponent(searchParams.get("title") || "");
					const description = decodeURIComponent(
						searchParams.get("description") || "",
					);
					const reasoning = decodeURIComponent(
						searchParams.get("reasoning") || "",
					);

					themePromptToSend = `FOCUSED REFLECTION TOPIC: ${title}

DESCRIPTION: ${description}

AI ANALYSIS REASONING: ${reasoning}

This reflection topic was specifically identified from the user's daily check-in patterns. Focus on helping them explore their personal experience with this topic in depth.`;
				}

				// Request a summary from the conversation
				const response = await aiService.askReflectionQuestion(
					updatedHistory,
					themePromptToSend,
				);

				// Extract summary from response or generate one
				let summary: { title?: string; description: string };

				if (response.summary) {
					summary = {
						title: response.summary.title,
						description: response.summary.description,
					};
				} else {
					// Fallback: generate summary using the summary service
					const summaryResponse =
						await aiService.generateReflectionSummary(updatedHistory);

					if (typeof summaryResponse === "string") {
						summary = {
							description: summaryResponse,
						};
					} else if (summaryResponse && typeof summaryResponse === "object") {
						if (
							"summary" in summaryResponse &&
							(summaryResponse as any).summary?.title &&
							(summaryResponse as any).summary?.description
						) {
							summary = {
								title: (summaryResponse as any).summary.title,
								description: (summaryResponse as any).summary.description,
							};
						} else if (
							"title" in summaryResponse &&
							"description" in summaryResponse
						) {
							summary = {
								title: (summaryResponse as any).title,
								description: (summaryResponse as any).description,
							};
						} else {
							summary = {
								description:
									"A reflection on my recent experiences and insights.",
							};
						}
					} else {
						summary = {
							description:
								"A reflection on my recent experiences and insights.",
						};
					}
				}

				// Show the conversation end summary component
				setConversationEndSummary(summary);
				setIsLoadingQuestion(false);
				return;
			} catch (error) {
				console.error("Error generating summary:", error);
				const errorMessage =
					error instanceof Error ? error.message : "AI service not available";
				setAiError(
					`Unable to generate reflection summary: ${errorMessage}. Please configure the AI service to use this feature.`,
				);
				// Clear loading state when there's an error
				setIsLoadingQuestion(false);
				return;
			}
		}

		// NORMAL FLOW: Continue asking questions (send all previous Q&As for context)
		setCurrentQuestion(""); // Clear current question to show only "thinking..." during loading
		try {
			// Check if this is an AI-recommended reflection for follow-up questions too
			const isAISuggested = searchParams.get("suggested") === "true";
			let themePromptToSend = selectedTheme?.prompt;

			if (isAISuggested) {
				// For AI-recommended reflections, construct the proper context for follow-ups
				const title = decodeURIComponent(searchParams.get("title") || "");
				const description = decodeURIComponent(
					searchParams.get("description") || "",
				);
				const reasoning = decodeURIComponent(
					searchParams.get("reasoning") || "",
				);

				themePromptToSend = `FOCUSED REFLECTION TOPIC: ${title}

DESCRIPTION: ${description}

AI ANALYSIS REASONING: ${reasoning}

This reflection topic was specifically identified from the user's daily check-in patterns. Focus on helping them explore their personal experience with this topic in depth.`;
			}

			const response = await aiService.askReflectionQuestion(
				updatedHistory,
				themePromptToSend,
			);

			if (response.question) {
				setCurrentQuestion(response.question);
				setConversationHistory([
					...updatedConversation,
					{ role: "assistant", content: response.question },
				]);

				// Update decision summary if provided (for multi-turn conversations)
				if (response.summary) {
					debugLog("📝 Updating decision summary:", response.summary);
					setDecisionSummary(response.summary);
				}

				// Auto-save disabled for new reflections: defer saving until QA complete
				// (intentionally not calling saveReflectionDraft here)

				// Clear current answer for next question
				setCurrentAnswer("");
			} else {
				throw new Error("AI service did not return a question");
			}
		} catch (error) {
			console.error("Error getting next question from AI:", error);
			const errorMessage =
				error instanceof Error ? error.message : String(error);
			setAiError(
				`AI conversation service is not properly configured: ${errorMessage}. Please contact support to resolve this issue.`,
			);

			setCurrentQuestion("");
		} finally {
			setCurrentAnswer("");
			setIsLoadingQuestion(false);
		}
	};

	// Handler for continuing to next step (Yes option)
	const handleContinueToNextStep = async () => {
		if (!conversationEndSummary) return;

		setIsProcessingSummary(true);
		// Generate full summary for the description step
		try {
			const summaryResponse =
				await aiService.generateReflectionSummary(qaHistory);

			let generatedTitle = "";
			let generatedDescription = "";

			if (typeof summaryResponse === "string") {
				const fallbackTitle =
					(reflectionTitle && reflectionTitle.trim()) || "My Reflection";
				generatedTitle = fallbackTitle;
				generatedDescription = summaryResponse;
			} else if (summaryResponse && typeof summaryResponse === "object") {
				if (
					"summary" in summaryResponse &&
					(summaryResponse as any).summary?.title &&
					(summaryResponse as any).summary?.description
				) {
					generatedTitle = (summaryResponse as any).summary.title;
					generatedDescription = (summaryResponse as any).summary.description;
				} else if (
					"title" in summaryResponse &&
					"description" in summaryResponse
				) {
					const obj = summaryResponse as { title: string; description: string };
					generatedTitle = obj.title;
					generatedDescription = obj.description;
				} else if (
					"response" in summaryResponse ||
					"text" in summaryResponse ||
					"content" in summaryResponse ||
					"answer" in summaryResponse
				) {
					const txt =
						(summaryResponse as any).response ||
						(summaryResponse as any).text ||
						(summaryResponse as any).content ||
						(summaryResponse as any).answer;
					const fallbackTitle =
						(reflectionTitle && reflectionTitle.trim()) || "My Reflection";
					generatedTitle = fallbackTitle;
					generatedDescription = String(txt);
				} else {
					generatedTitle =
						conversationEndSummary.title ||
						(reflectionTitle && reflectionTitle.trim()) ||
						"My Reflection";
					generatedDescription = conversationEndSummary.description;
				}
			} else {
				generatedTitle =
					conversationEndSummary.title ||
					(reflectionTitle && reflectionTitle.trim()) ||
					"My Reflection";
				generatedDescription = conversationEndSummary.description;
			}

			// Update state with generated content
			setReflectionTitle(generatedTitle);
			setReflectionDescription(generatedDescription);

			// Save immediately with the generated data
			const isGenericSummary =
				generatedDescription ===
				"A reflection on my recent experiences and insights.";
			if (!isGenericSummary) {
				await saveReflectionDraft("AI generated reflection summary", {
					title: generatedTitle,
					description: generatedDescription,
				});
				showNotification("Your Reflection Summary was saved");
			}

			// Mark that we're transitioning from AI summary to prevent draft reload
			isTransitioningFromAIRef.current = true;

			// Clear the conversation end summary and reset diving deeper flag
			setConversationEndSummary(null);
			setIsDivingDeeper(false);

			// Navigate to description step
			setCurrentStep("description");

			// Clear transition flag after successful navigation
			setTimeout(() => {
				isTransitioningFromAIRef.current = false;
			}, 500);
		} catch (error) {
			console.error("Error generating summary:", error);
			setAiError(
				`Unable to generate reflection summary: ${error instanceof Error ? error.message : "Unknown error"}`,
			);
		} finally {
			setIsProcessingSummary(false);
		}
	};

	// Handler for diving deeper (ask 4-5 more questions)
	const handleDiveDeeper = async () => {
		setConversationEndSummary(null);
		setIsDivingDeeper(true);
		setIsLoadingQuestion(true);
		setAiError(null);

		// Continue with the conversation - ask next question
		try {
			const isAISuggested = searchParams.get("suggested") === "true";
			let themePromptToSend = selectedTheme?.prompt;

			if (isAISuggested) {
				const title = decodeURIComponent(searchParams.get("title") || "");
				const description = decodeURIComponent(
					searchParams.get("description") || "",
				);
				const reasoning = decodeURIComponent(
					searchParams.get("reasoning") || "",
				);

				themePromptToSend = `FOCUSED REFLECTION TOPIC: ${title}

DESCRIPTION: ${description}

AI ANALYSIS REASONING: ${reasoning}

This reflection topic was specifically identified from the user's daily check-in patterns. Focus on helping them explore their personal experience with this topic in depth.`;
			}

			const response = await aiService.askReflectionQuestion(
				qaHistory,
				themePromptToSend,
			);

			if (response.question) {
				setCurrentQuestion(response.question);
				setConversationHistory([
					...conversationHistory,
					{ role: "assistant", content: response.question },
				]);
				setCurrentAnswer("");
			} else {
				throw new Error("AI service did not return a question");
			}
		} catch (error) {
			console.error("Error getting deeper question from AI:", error);
			const errorMessage =
				error instanceof Error ? error.message : String(error);
			setAiError(`AI conversation service error: ${errorMessage}`);
		} finally {
			setIsLoadingQuestion(false);
		}
	};

	// Handler for starting a different path (clear and restart)
	const handleDifferentPath = () => {
		// Clear all conversation state
		setQaHistory([]);
		setConversationHistory([]);
		setCurrentQuestion("");
		setCurrentAnswer("");
		setConversationEndSummary(null);
		setDecisionSummary(null);
		setIsDivingDeeper(false);
		setAiError(null);

		// Restart the conversation
		beginQuestions();
	};

	const generateContributingFactors = async () => {
		setIsLoadingFactors(true);
		try {
			// Ensure we have the required data
			if (!reflectionTitle || !reflectionDescription) {
				throw new Error(
					"Missing reflection title or description. Please complete the previous step.",
				);
			}

			/*console.log("🔍 Contributing Factors Debug - Starting generation with data:", {
        title: reflectionTitle,
        description: reflectionDescription,
        qaHistoryLength: qaHistory.length,
      });
      */
			const factors = await aiService.generateContributingFactors({
				title: reflectionTitle,
				description: reflectionDescription,
				qaResults: qaHistory,
				existingFactors: contributingFactors.map((f) => ({
					title: f.title,
					description: f.description,
				})),
			});

			//console.log("🔍 Contributing Factors Debug - Raw AI response:", factors);
			//console.log("��� Contributing Factors Debug - Response type:", typeof factors);
			//console.log("🔍 Contributing Factors Debug - Is array:", Array.isArray(factors));

			// Handle case where AI returns non-array response
			let factorsArray: Array<{ title: string; description: string }> = [];

			if (Array.isArray(factors)) {
				factorsArray = factors;
				console.log(
					"�� Contributing Factors Debug - Using array response:",
					factorsArray,
				);
			} else if (
				factors &&
				typeof factors === "object" &&
				Array.isArray((factors as any).actions)
			) {
				factorsArray = (factors as any).actions as Array<{
					title: string;
					description: string;
				}>;
			} else if (
				factors &&
				typeof factors === "object" &&
				Array.isArray((factors as any).corrective_actions)
			) {
				factorsArray = (factors as any).corrective_actions as Array<{
					title: string;
					description: string;
				}>;
			} else if (
				factors &&
				typeof factors === "object" &&
				((factors as any).response || (factors as any).text)
			) {
				const respText = (factors as any).response || (factors as any).text;
				console.log(
					"�� Contributing Factors Debug - Parsing text response:",
					respText,
				);
				// Parse the text response into structured factors
				factorsArray = parseContributingFactorsText(String(respText));
				debugLog(
					"🔍 Contributing Factors Debug - Parsed factors:",
					factorsArray,
				);
			} else if (typeof factors === "string") {
				console.warn(
					"AI returned text instead of JSON array for factors:",
					factors,
				);
				// Parse the text response into structured factors
				factorsArray = parseContributingFactorsText(factors);
			} else {
				debugWarn(
					"🔍 Contributing Factors Debug - Unexpected response format:",
					factors,
				);
				factorsArray = [];
			}

			const factorsWithIds = factorsArray.map((factor, index) => ({
				...factor,
				id: `factor-${contributingFactors.length + index}`,
				selected: false,
			}));

			debugLog(
				"🔍 Contributing Factors Debug - Final factors with IDs:",
				factorsWithIds,
			);
			// Append new factors to existing ones instead of replacing
			setContributingFactors((prev) => [...prev, ...factorsWithIds]);

			// No autosave here; user must click Next/Save to persist new reflections

			if (factorsWithIds.length === 0) {
				debugWarn(
					"🔍 Contributing Factors Debug - No factors generated, showing alert",
				);
				alert(
					"No contributing factors were generated. This might be due to AI service configuration issues.",
				);
			}
		} catch (error) {
			debugError(
				"🔍 Contributing Factors Debug - Error during generation:",
				error,
			);
			// Keep existing factors, don't clear them on error
			// setContributingFactors([]);  // Removed - this was clearing all existing factors
			alert(
				`Unable to generate new contributing factors: ${error instanceof Error ? error.message : "Unknown error"}`,
			);
		} finally {
			setIsLoadingFactors(false);
		}
	};

	const generateSignals = async () => {
		setIsLoadingSignals(true);
		setAiError(null);

		console.log("Starting signals generation with data:", {
			title: reflectionTitle,
			description: reflectionDescription,
			qaHistoryLength: qaHistory.length,
		});

		try {
			// Check if we have the required data
			if (!reflectionTitle || !reflectionDescription) {
				throw new Error(
					"Missing reflection title or description. Please complete the previous step.",
				);
			}

			const signalsData: any = await aiService.generateSignals({
				title: reflectionTitle,
				description: reflectionDescription,
				qaResults: qaHistory,
				existingSignals: signals.map((s) => ({
					title: s.title,
					description: s.description,
				})),
			});

			//console.log("Received signals data:", signalsData);

			// Handle case where AI returns non-array response
			let signalsArray: Array<{ title: string; description: string }> = [];

			if (Array.isArray(signalsData)) {
				signalsArray = signalsData;
			} else if (
				signalsData &&
				typeof signalsData === "object" &&
				((signalsData as any).response || (signalsData as any).text)
			) {
				//console.log("��� Signals Debug - Parsing text response:", signalsData.response);
				// Parse the text response into structured signals
				signalsArray = parseContributingFactorsText(
					String((signalsData as any).response || (signalsData as any).text),
				);
				//console.log("🔍 Signals Debug - Parsed signals:", signalsArray);
			} else if (typeof signalsData === "string") {
				// Try to extract signals from text response
				console.warn(
					"AI returned text instead of JSON array for signals:",
					signalsData,
				);
				// Parse the text response into structured signals
				signalsArray = parseContributingFactorsText(signalsData);
			} else {
				console.warn(
					"Unexpected signals data format, no fallback data provided",
				);
				signalsArray = [];
			}

			const signalsWithIds = signalsArray.map((signal, index) => ({
				...signal,
				id: `signal-${signals.length + index}`,
				selected: false,
			}));

			//console.log("Setting signals:", signalsWithIds);
			// Append new signals to existing ones instead of replacing
			setSignals((prev) => [...prev, ...signalsWithIds]);

			// No autosave here; user must click Next/Save to persist new reflections
		} catch (error) {
			console.error("Error generating signals:", error);
			setAiError(
				`Failed to generate signals: ${error instanceof Error ? error.message : "Unknown error"}`,
			);

			// Keep existing signals, don't clear them on error
			// setSignals([]);  // Removed - this was clearing all existing signals
			alert(
				"Unable to generate new signals. Please check your AI service configuration.",
			);
		} finally {
			setIsLoadingSignals(false);
		}
	};

	const generateFutureState = async () => {
		setIsLoadingFuture(true);
		try {
			// Send complete context: current state, chosen signals, and contributing factors
			const selectedFactors = contributingFactors.filter((f) => f.selected);
			const selectedSignals = signals.filter((s) => s.selected);

			const contextualInput = `Current Reflection: ${reflectionTitle}
Description: ${reflectionDescription}

Selected Contributing Factors:
${selectedFactors.map((f) => `- ${f.title}: ${f.description}`).join("\n")}

Selected Signals:
${selectedSignals.map((s) => `- ${s.title}: ${s.description}`).join("\n")}

Based on this information, describe how life would look like if this problem was addressed.`;

			const future = await aiService.generateFutureState(contextualInput);

			if (typeof future === "string") {
				setFutureState(future);
				await saveReflectionDraft("AI generated future state", {
					future_state: future,
				});
			} else if (future && typeof future === "object") {
				const futureText =
					(future as any).description ||
					(future as any).text ||
					(future as any).content ||
					(future as any).response ||
					(future as any).answer ||
					(future as any).future_state ||
					(future as any).future ||
					JSON.stringify(future);
				setFutureState(futureText);
				await saveReflectionDraft("AI generated future state", {
					future_state: futureText,
				});
			} else {
				console.warn("🚨 Unexpected future state format:", {
					future,
					type: typeof future,
				});
				setFutureState(
					"I was unable to generate a future state description at this time. Please try again or describe your ideal future state manually.",
				);
			}
		} catch (error) {
			console.error("Error generating future state:", error);
			setFutureState("");
			alert(
				`Unable to generate future state: ${error instanceof Error ? error.message : "AI service not available"}`,
			);
		} finally {
			setIsLoadingFuture(false);
		}
	};

	const generateCorrectiveActions = async () => {
		setIsLoadingActions(true);
		setAiError(null);

		//console.log("Starting corrective actions generation");

		try {
			// Send complete context: current state, future state, signals and contributing factors
			const selectedFactors = contributingFactors.filter((f) => f.selected);
			const selectedSignals = signals.filter((s) => s.selected);

			/* console.log("Corrective actions generation data:", {
        title: reflectionTitle,
        description: reflectionDescription,
        futureState: futureState,
        selectedFactors: selectedFactors.length,
        selectedSignals: selectedSignals.length,
      });*/

			// Validate we have the required data
			if (!reflectionTitle || !reflectionDescription) {
				throw new Error("Missing reflection title or description");
			}

			if (!futureState) {
				throw new Error(
					"Future state is required for generating corrective actions",
				);
			}

			if (selectedFactors.length === 0 && selectedSignals.length === 0) {
				console.warn("No factors or signals selected, using defaults");
			}

			const actions: any = await aiService.generateCorrectiveActions({
				title: reflectionTitle,
				description: reflectionDescription,
				futureState: futureState,
				contributingFactors: selectedFactors,
				signals: selectedSignals,
				existingActions: correctiveActions.map((a) => ({
					title: a.title,
					description: a.description,
				})),
			});

			console.log("���� Received corrective actions data:", actions);
			console.log("🔍 Raw response type:", typeof actions);
			console.log("🔍 Is array:", Array.isArray(actions));
			if (actions && typeof actions === "object") {
				console.log("🔍 Object keys:", Object.keys(actions));
				console.log("🔍 Full object:", JSON.stringify(actions, null, 2));
			}

			// Handle case where AI returns non-array response more robustly
			let actionsArray: Array<{ title: string; description: string }> = [];

			if (Array.isArray(actions as any)) {
				actionsArray = actions;
				console.log("✅ Got array response directly");
			} else if (actions && typeof actions === "object") {
				// Try various possible fields that might contain the actions
				const actionsText =
					(actions as any).response ||
					(actions as any).text ||
					(actions as any).content ||
					(actions as any).answer ||
					(actions as any).actions ||
					(actions as any).corrective_actions ||
					JSON.stringify(actions);

				console.log(
					"�� Corrective Actions Debug - Parsing text response:",
					actionsText,
				);
				console.log("🔍 Text length:", actionsText?.length);

				try {
					// Parse the text response into structured actions
					actionsArray = parseContributingFactorsText(actionsText);
					console.log(
						"🔍 Corrective Actions Debug - Parsed actions:",
						actionsArray,
					);
					console.log("������ Parsed actions count:", actionsArray?.length);
				} catch (parseError: any) {
					console.error("❌ Failed to parse actions text:", parseError);
					console.error("�� Failed text was:", actionsText);
					console.error(
						"❌ Parse error details:",
						parseError instanceof Error
							? parseError.message
							: String(parseError),
					);
					actionsArray = [];
				}
			} else if (typeof actions === "string") {
				console.log("�� Got string response, attempting to parse");
				console.log("🔍 String content:", actions);
				try {
					actionsArray = parseContributingFactorsText(actions);
					console.log("🔍 Parsed from string:", actionsArray);
				} catch (parseError: any) {
					console.error("❌ Failed to parse string actions:", parseError);
					console.error("❌ String was:", actions);
					actionsArray = [];
				}
			} else {
				console.error("🚨 AI returned unexpected format:");
				console.error("🚨 Value:", actions);
				console.error("🚨 Type:", typeof actions);
				console.error("���� Constructor:", (actions as any)?.constructor?.name);
				actionsArray = [];
			}

			// Throw error if no valid actions received
			if (!Array.isArray(actionsArray) || actionsArray.length === 0) {
				throw new Error(
					"AI service failed to generate corrective actions. Please check AI service configuration.",
				);
			}

			const formattedActions = actionsArray.map((action, index) => ({
				id: `action-${correctiveActions.length + index}`,
				title: action.title || `Action ${correctiveActions.length + index + 1}`,
				description: action.description || "No description available",
				selected: false,
			}));

			// Append new actions to existing ones instead of replacing
			setCorrectiveActions((prev) => [...prev, ...formattedActions]);
			console.log("Set corrective actions:", formattedActions.length);

			// No autosave here; user must click Next/Save to persist new reflections
		} catch (error) {
			console.error("Error generating corrective actions:", error);
			const errorMessage =
				error instanceof Error ? error.message : String(error);
			setAiError(
				`AI service unavailable - using suggested actions: ${errorMessage}`,
			);

			// Keep existing actions, don't clear them on error
			// setCorrectiveActions([]);  // Removed - this was clearing all existing actions
		} finally {
			setIsLoadingActions(false);
		}
	};

	const getSelectedCount = (items: { selected: boolean }[]) => {
		return items.filter((item) => item.selected).length;
	};

	const toggleSelection = (
		items: any[],
		setItems: React.Dispatch<React.SetStateAction<any[]>>,
		id: string,
		maxSelections: number = 3,
	) => {
		const currentSelected = getSelectedCount(items);
		const item = items.find((i) => i.id === id);

		if (!item.selected && currentSelected >= maxSelections) {
			return; // Can't select more than max
		}

		setItems(
			items.map((item) =>
				item.id === id ? { ...item, selected: !item.selected } : item,
			),
		);
	};

	const addNewItem = (
		items: any[],
		setItems: React.Dispatch<React.SetStateAction<any[]>>,
		type: "factor" | "signal" | "action",
	) => {
		const newItem = {
			id: `${type}-${Date.now()}`,
			title: "",
			description: "",
			selected: false,
			isEditing: true,
		};
		setItems([...items, newItem]);
	};

	const parseContributingFactorsText = (
		text: string,
	): Array<{ title: string; description: string }> => {
		console.log(
			"🔍 Parsing contributing factors text:",
			text?.substring(0, 200) + "...",
		);

		const factors: Array<{ title: string; description: string }> = [];

		if (!text || typeof text !== "string") {
			console.error("❌ Invalid text input for parsing:", text);
			return factors;
		}

		// First try to parse as JSON if it looks like JSON
		if (text.trim().startsWith("[") || text.trim().startsWith("{")) {
			try {
				console.log("🔍 Attempting JSON parse...");
				const parsed = JSON.parse(text);
				if (Array.isArray(parsed)) {
					console.log("✅ Successfully parsed as JSON array");
					return (
						parsed as Array<{ title: string; description: string }>
					).filter(
						(item: { title?: string; description?: string }) =>
							!!item.title && !!item.description,
					);
				} else if (
					(parsed as any).actions &&
					Array.isArray((parsed as any).actions)
				) {
					console.log("��� Successfully parsed JSON with actions property");
					return (
						(parsed as any).actions as Array<{
							title: string;
							description: string;
						}>
					).filter(
						(item: { title?: string; description?: string }) =>
							!!item.title && !!item.description,
					);
				} else if (
					(parsed as any).corrective_actions &&
					Array.isArray((parsed as any).corrective_actions)
				) {
					console.log(
						"✅ Successfully parsed JSON with corrective_actions property",
					);
					return (
						(parsed as any).corrective_actions as Array<{
							title: string;
							description: string;
						}>
					).filter(
						(item: { title?: string; description?: string }) =>
							!!item.title && !!item.description,
					);
				}
			} catch (jsonError) {
				console.log("⚠️ JSON parse failed, falling back to text parsing");
			}
		}

		// Split by numbered sections (1., 2., 3., etc.)
		console.log("🔍 Attempting numbered section parsing...");
		const sections = text
			.split(/\d+\.\s+/)
			.filter((section) => section.trim().length > 0);
		console.log("🔍 Found sections:", sections.length);

		sections.forEach((section, index) => {
			const lines = section
				.trim()
				.split("\n")
				.filter((line) => line.trim().length > 0);

			if (lines.length > 0) {
				// First line should be the title (remove trailing colon if present)
				const title = lines[0].replace(/:\s*$/, "").trim();

				// Rest of the lines are the description
				const descriptionLines = lines
					.slice(1)
					.map((line) => {
						// Remove bullet points and dashes
						return line.replace(/^\s*[-•]\s*/, "").trim();
					})
					.filter((line) => line.length > 0);

				const description =
					descriptionLines.join(" ") || "No description provided";

				if (title) {
					factors.push({
						title,
						description,
					});
					console.log(`✅ Parsed item ${index + 1}: "${title}"`);
				}
			}
		});

		// If numbered parsing failed, try bullet point parsing
		if (factors.length === 0) {
			console.log("🔍 Numbered parsing failed, trying bullet point parsing...");
			const bulletSections = text
				.split(/[-•*]\s+/)
				.filter((section) => section.trim().length > 0);

			bulletSections.forEach((section, index) => {
				const lines = section
					.trim()
					.split("\n")
					.filter((line) => line.trim().length > 0);
				if (lines.length > 0) {
					const title = lines[0].replace(/:\s*$/, "").trim();
					const description =
						lines.slice(1).join(" ") || "No description provided";

					if (title) {
						factors.push({ title, description });
						console.log(`✅ Parsed bullet item ${index + 1}: "${title}"`);
					}
				}
			});
		}

		// If still nothing, try colon-separated parsing
		if (factors.length === 0) {
			console.log(
				"🔍 Bullet parsing failed, trying colon-separated parsing...",
			);
			const colonSections = text
				.split("\n")
				.filter((line) => line.includes(":") && line.trim().length > 0);

			colonSections.forEach((line, index) => {
				const [title, ...descParts] = line.split(":");
				const description =
					descParts.join(":").trim() || "No description provided";

				if (title && title.trim()) {
					factors.push({
						title: title.trim(),
						description,
					});
					console.log(`✅ Parsed colon item ${index + 1}: "${title.trim()}"`);
				}
			});
		}

		console.log(`🔍 Final parsed factors count: ${factors.length}`);
		return factors;
	};

	const updateItem = (
		items: any[],
		setItems: React.Dispatch<React.SetStateAction<any[]>>,
		id: string,
		field: string,
		value: string,
	) => {
		setItems(
			items.map((item) =>
				item.id === id ? { ...item, [field]: value } : item,
			),
		);
	};

	const goNext = async () => {
		const stepOrder: ReflectionStep[] = [
			"intro",
			"questions",
			"description",
			"contributing-factors-intro",
			"contributing-factors",
			"signals-intro",
			"signals",
			"future-state",
			"corrective-actions-intro",
			"corrective-actions",
			"linking",
			"summary",
		];
		const currentIndex = stepOrder.indexOf(currentStep);
		if (currentIndex < stepOrder.length - 1) {
			const nextStep = stepOrder[currentIndex + 1];

			// Auto-generate content when moving to certain steps
			//console.log(`Moving to step: ${nextStep}`);
			if (nextStep === "contributing-factors") {
				// Defer generation to effect watcher which triggers deterministically when both step and data are available
				// (no timers)
			} else if (nextStep === "signals") {
				// Defer signals generation to effect watcher
			} else if (nextStep === "future-state") {
				console.log("Triggering future state generation");
				generateFutureState();
			} else if (nextStep === "corrective-actions") {
				console.log("Triggering corrective actions generation");
				generateCorrectiveActions();
			} else if (nextStep === "linking") {
				// Initialize linking state
				const selectedActions = correctiveActions.filter((a) => a.selected);
				setActionLinks(
					selectedActions.map((action) => ({
						correctiveActionId: action.id,
						linkedFactors: [],
						linkedSignals: [],
					})),
				);
				setCurrentLinkingIndex(0);
			}

			// IMPORTANT: Save reflection BEFORE transitioning to summary step
			// This ensures the reflection is fully persisted before auto-completion triggers
			if (nextStep === "summary") {
				console.log(
					"📝 Saving reflection before transitioning to summary step",
				);
				await saveReflectionDraft("Saving before summary step");
			}

			setCurrentStep(nextStep);

			// Save when user clicks Next (user-initiated navigation) for non-summary steps
			if (nextStep !== "summary") {
				await saveReflectionDraft("User clicked Next button");
			}
		}
	};

	// When moving to contributing-factors step, generate factors deterministically when data is available
	const hasGeneratedContributingRef = useRef(false);
	useEffect(() => {
		if (
			currentStep === "contributing-factors" &&
			!hasGeneratedContributingRef.current
		) {
			if (reflectionTitle && reflectionDescription) {
				hasGeneratedContributingRef.current = true;
				console.log(
					"Step contributing-factors reached and data ready — generating contributing factors",
				);
				generateContributingFactors();
			} else {
				console.log(
					"contributing-factors step reached but reflection data missing; waiting for data",
				);
			}
		}
	}, [currentStep, reflectionTitle, reflectionDescription]);

	// When moving to signals step, generate signals deterministically when data is available
	const hasGeneratedSignalsRef = useRef(false);
	useEffect(() => {
		if (currentStep === "signals" && !hasGeneratedSignalsRef.current) {
			if (reflectionTitle && reflectionDescription) {
				hasGeneratedSignalsRef.current = true;
				console.log("Step signals reached and data ready — generating signals");
				generateSignals();
			} else {
				console.log(
					"signals step reached but reflection data missing; waiting for data",
				);
			}
		}
	}, [currentStep, reflectionTitle, reflectionDescription]);

	// When moving to linking step, initialize actionLinks if not already initialized
	useEffect(() => {
		if (currentStep === "linking" && actionLinks.length === 0) {
			const selectedActions = correctiveActions.filter((a) => a.selected);
			if (selectedActions.length > 0) {
				console.log("Step linking reached — initializing actionLinks", {
					selectedActionsCount: selectedActions.length,
					totalActions: correctiveActions.length,
				});
				setActionLinks(
					selectedActions.map((action) => ({
						correctiveActionId: action.id,
						linkedFactors: [],
						linkedSignals: [],
					})),
				);
				setCurrentLinkingIndex(0);
			} else {
				console.log("Step linking reached but no selected actions yet", {
					totalActions: correctiveActions.length,
					selectedActions: correctiveActions.filter((a) => a.selected).length,
				});
			}
		}
	}, [currentStep, correctiveActions, actionLinks.length]);

	const goBack = () => {
		const stepOrder: ReflectionStep[] = [
			"intro",
			"questions",
			"description",
			"contributing-factors-intro",
			"contributing-factors",
			"signals-intro",
			"signals",
			"future-state",
			"corrective-actions-intro",
			"corrective-actions",
			"linking",
			"summary",
		];
		const currentIndex = stepOrder.indexOf(currentStep);
		if (currentIndex > 0) {
			setCurrentStep(stepOrder[currentIndex - 1]);
		}
	};

	const getProgressValue = () => {
		const stepValues: Record<ReflectionStep, number> = {
			intro: 0,
			questions: 15,
			description: 25,
			"contributing-factors-intro": 35,
			"contributing-factors": 45,
			"signals-intro": 55,
			signals: 65,
			"future-state": 75,
			"corrective-actions-intro": 80,
			"corrective-actions": 85,
			linking: 95,
			summary: 100,
		};
		return stepValues[currentStep] || 0;
	};

	const shouldShowProgress = [
		"contributing-factors-intro",
		"contributing-factors",
		"signals-intro",
		"signals",
		"future-state",
		"corrective-actions-intro",
		"corrective-actions",
		"linking",
	].includes(currentStep);

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
			<div className="container mx-auto px-4 py-6 sm:py-8 max-w-7xl">
				{shouldShowProgress && (
					<div className="mb-6">
						<div className="flex items-center justify-between mb-2">
							<Button
								variant="ghost"
								onClick={goBack}
								className="flex items-center"
							>
								<ArrowLeft className="h-4 w-4 mr-2" />
								Back
							</Button>
							<div className="flex items-center space-x-2">
								{isSaving && (
									<div className="flex items-center text-sm text-muted-foreground">
										<Loader2 className="h-3 w-3 mr-1 animate-spin" />
										Saving...
									</div>
								)}
								{lastSaved && !isSaving && (
									<div className="text-xs text-muted-foreground">
										Saved {lastSaved.toLocaleTimeString()}
									</div>
								)}
								{isDraft && (
									<Badge variant="outline" className="text-xs">
										Draft
									</Badge>
								)}
								<Button
									variant="ghost"
									size="sm"
									onClick={() => setShowReminder(true)}
									aria-label="Show current reflection text"
								>
									<Lightbulb className="h-4 w-4" />
								</Button>
							</div>
						</div>
						<Progress value={getProgressValue()} className="h-2" />
					</div>
				)}

				<Dialog open={showReminder} onOpenChange={setShowReminder}>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>Current Reflection</DialogTitle>
							<DialogDescription>
								A quick reminder of your reflection text so far.
							</DialogDescription>
						</DialogHeader>
						<div className="space-y-3 max-h-[50vh] overflow-auto">
							<h3 className="font-medium text-foreground">
								{reflectionTitle || "Untitled Reflection"}
							</h3>
							<p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
								{reflectionDescription &&
								reflectionDescription.trim().length > 0
									? reflectionDescription
									: "No reflection text captured yet. Add your thoughts in the Description step."}
							</p>
						</div>
						<DialogFooter>
							<Button onClick={() => setShowReminder(false)}>Close</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Notification Toast */}
				{notification && (
					<div
						className={`fixed top-4 right-4 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium z-50 animate-in fade-in slide-in-from-top-2 transition-all ${
							notification.type === "success"
								? "bg-green-500"
								: notification.type === "error"
									? "bg-red-500"
									: "bg-blue-500"
						}`}
					>
						{notification.message}
					</div>
				)}

				<Card className="border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
					{currentStep === "intro" && (
						<>
							<CardHeader className="text-center pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
								<div className="flex items-center justify-center gap-3 mb-3">
									<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
										<Brain className="h-5 w-5 text-white" />
									</div>
								</div>
								<CardTitle className="text-2xl sm:text-3xl font-semibold">
									{assignedTopic
										? `Assigned Topic: ${assignedTopic.title}`
										: "Create a new Reflection"}
								</CardTitle>
								<CardDescription>
									{assignedTopic ? (
										<>
											<div className="mb-4 p-4 bg-blue-50 border-l-4 border-blue-200 rounded">
												<p className="text-blue-800 font-medium">
													Your mentor has assigned this reflection topic:
												</p>
												{assignedTopic.description && (
													<p className="text-blue-700 mt-2">
														{assignedTopic.description}
													</p>
												)}
											</div>
											<p>
												We'll guide you through questions specifically focused
												on this topic to help you explore and understand it
												better.
											</p>
										</>
									) : (
										<>
											Life can sometimes feel overwhelming, and understanding
											what causes us stress is the first step to managing it.
											We'll guide you through a series of questions to help you
											explore the things in your life that may be contributing
											to stress. By reflecting on these questions, you'll gain
											clearer insights into your reflections, empowering you to
											address them with confidence and clarity.
										</>
									)}
								</CardDescription>
							</CardHeader>
							<CardContent>
								{assignedTopic ? (
									<div className="text-center">
										<p className="text-muted-foreground mb-8">
											Let's take the journey toward a more balanced and
											productive life.
										</p>
										<Button
											data-testid="start-topic-reflection-button"
											size="lg"
											className="w-full max-w-sm bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-md hover:shadow-lg transition-all"
											onClick={() => startReflection(assignedTopic?.title)}
										>
											Start Topic Reflection
										</Button>
									</div>
								) : (
									<Tabs
										defaultValue={searchParams.get("tab") || "general"}
										className="w-full"
									>
										<TabsList className="grid w-full grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-0 h-auto sm:h-10">
											<TabsTrigger
												data-testid="general-tab"
												value="general"
												className="py-3 sm:py-2 text-sm sm:text-base whitespace-nowrap"
											>
												General
											</TabsTrigger>
											<TabsTrigger
												data-testid="themes-tab"
												value="themes"
												className="py-3 sm:py-2 text-sm sm:text-base whitespace-nowrap"
											>
												Theme Based
											</TabsTrigger>
											<TabsTrigger
												data-testid="checkins-tab"
												value="checkins"
												className="py-3 sm:py-2 text-sm sm:text-base whitespace-nowrap"
											>
												Based on Check-ins
											</TabsTrigger>
										</TabsList>

										<TabsContent value="general" className="mt-6">
											<div className="text-center space-y-6">
												<p className="text-muted-foreground">
													Let's take the journey toward a more balanced and
													productive life.
												</p>
												<Button
													data-testid="start-new-reflection-button"
													size="lg"
													className="w-full max-w-sm bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-md hover:shadow-lg transition-all"
													onClick={() => startReflection()}
												>
													Start a new reflection
												</Button>

												{/* Draft Reflections List */}
												<DraftReflectionsList
													onContinueDraft={handleContinueDraft}
												/>

												{/* Assigned Topics Section */}
												<div className="mt-8">
													<AssignedTopics
														onTopicSelect={(topic) => {
															// Navigate to reflection flow with the selected topic
															window.location.href = `/reflection/new?topic=${topic.topic_id}`;
														}}
														showStartButton={true}
													/>
												</div>
											</div>
										</TabsContent>

										<TabsContent value="themes" className="mt-6">
											<Card className="border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
												<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
													<div className="flex items-center gap-3">
														<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
															<Lightbulb className="h-5 w-5 text-white" />
														</div>
														<div>
															<CardTitle className="text-xl sm:text-2xl font-semibold">
																Theme Based Starting Points
															</CardTitle>
															<CardDescription className="mt-1">
																Choose from predefined themes to guide your
																reflection
															</CardDescription>
														</div>
													</div>
												</CardHeader>
												<CardContent className="pt-6">
													<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
														{reflectionThemes.length > 0 ? (
															reflectionThemes.map((theme) => {
																const ThemeIcon = getThemeIcon(theme.name);
																return (
																	<Card
																		key={theme.id}
																		data-testid="theme-card"
																		className="border-2 shadow-md hover:shadow-xl transition-all duration-200 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm cursor-pointer"
																		onClick={() => {
																			setSelectedTheme(theme);
																			startReflection(theme.name, theme);
																		}}
																	>
																		<CardContent className="p-4">
																			<div className="flex items-start gap-3">
																				<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex-shrink-0">
																					<ThemeIcon className="h-4 w-4 text-white" />
																				</div>
																				<div className="flex-1 min-w-0">
																					<h4 className="font-semibold text-foreground mb-2 break-words">
																						{theme.name}
																					</h4>
																					<p className="text-sm text-muted-foreground break-words leading-relaxed">
																						{theme.description}
																					</p>
																				</div>
																			</div>
																		</CardContent>
																	</Card>
																);
															})
														) : (
															<div className="col-span-2 text-center py-8">
																<Lightbulb className="h-12 w-12 text-gray-400 mx-auto mb-4" />
																<p className="text-foreground font-medium mb-2">
																	No themes available.
																</p>
																<p className="text-sm text-muted-foreground">
																	Ask your mentor to configure reflection
																	themes.
																</p>
															</div>
														)}
													</div>
												</CardContent>
											</Card>
										</TabsContent>

										<TabsContent value="checkins" className="mt-6">
											<Card className="border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
												<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
													<div className="flex items-center gap-3">
														<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
															<Brain className="h-5 w-5 text-white" />
														</div>
														<div>
															<CardTitle className="text-xl sm:text-2xl font-semibold">
																Based on Your Check-ins
															</CardTitle>
															<CardDescription className="mt-1">
																AI-powered suggestions based on patterns in your
																daily check-ins
															</CardDescription>
														</div>
													</div>
												</CardHeader>
												<CardContent className="pt-6">
													{suggestedReflection ? (
														<Card className="border-2 shadow-md bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
															<CardContent className="p-6">
																<div className="flex items-start space-x-3">
																	<div className="p-2 rounded-lg bg-gradient-to-br from-yellow-400 to-orange-500 flex-shrink-0">
																		<Lightbulb className="h-5 w-5 text-white" />
																	</div>
																	<div className="flex-1 text-left">
																		<h5 className="font-semibold text-lg mb-3 text-foreground">
																			{suggestedReflection.title}
																		</h5>
																		<p className="text-sm mb-4 text-muted-foreground leading-relaxed">
																			{suggestedReflection.description}
																		</p>
																		{suggestedReflection.reasoning && (
																			<details className="mb-4">
																				<summary className="text-sm cursor-pointer hover:text-foreground text-muted-foreground font-medium">
																					Why this reflection?
																				</summary>
																				<p className="text-sm mt-3 pl-4 border-l-2 border-yellow-200 dark:border-yellow-800 text-muted-foreground leading-relaxed">
																					{suggestedReflection.reasoning}
																				</p>
																			</details>
																		)}
																		<Button
																			data-testid="start-suggested-reflection-button"
																			size="lg"
																			className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-md hover:shadow-lg transition-all"
																			onClick={() => {
																				const queryParams = new URLSearchParams(
																					{
																						suggested: "true",
																						tab: "checkins",
																						title: suggestedReflection.title,
																						description:
																							suggestedReflection.description,
																						reasoning:
																							suggestedReflection.reasoning ||
																							"",
																					},
																				);
																				navigate(
																					`/reflection/new?${queryParams.toString()}`,
																				);
																			}}
																		>
																			Start This Suggested Reflection
																		</Button>
																	</div>
																</div>
															</CardContent>
														</Card>
													) : (
														<div className="text-center py-8">
															<div className="p-3 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 w-fit mx-auto mb-4">
																<Lightbulb className="h-8 w-8 text-white" />
															</div>
															<h4 className="font-semibold text-foreground mb-2">
																No suggestions available yet
															</h4>
															<p className="text-sm text-muted-foreground mb-4">
																Complete more daily check-ins to get AI-powered
																reflection suggestions based on your patterns
															</p>
															<Button
																data-testid="view-checkins-button"
																variant="outline"
																onClick={() => navigate("/check-ins")}
																className="bg-white dark:bg-slate-800"
															>
																View Your Check-ins
															</Button>
														</div>
													)}
												</CardContent>
											</Card>
										</TabsContent>
									</Tabs>
								)}
							</CardContent>
						</>
					)}

					{currentStep === "questions" && (
						<>
							{conversationEndSummary ? (
								<ConversationEndSummary
									summary={conversationEndSummary}
									onContinue={handleContinueToNextStep}
									onDiveDeeper={handleDiveDeeper}
									onDifferentPath={handleDifferentPath}
									isLoading={isLoadingQuestion}
									isProcessingSummary={isProcessingSummary}
									isDivingDeeper={isDivingDeeper}
								/>
							) : (
								<QAStep
									currentQuestion={currentQuestion}
									currentAnswer={currentAnswer}
									qaHistory={qaHistory}
									isLoadingQuestion={isLoadingQuestion}
									aiError={aiError}
									decisionSummary={decisionSummary}
									onAnswerChange={setCurrentAnswer}
									onSubmitAnswer={submitAnswer}
									subtitle="Carefully consider and answer the questions about your reflection"
								/>
							)}
						</>
					)}

					{currentStep === "description" && (
						<div data-testid="reflection-description-container">
							<DescriptionStep
								title={reflectionTitle}
								description={reflectionDescription}
								onTitleChange={setReflectionTitle}
								onDescriptionChange={setReflectionDescription}
								onSave={async () => {
									try {
										await saveReflectionDraft("User saved reflection summary");
										showNotification("Your Reflection Summary was saved");
									} catch (error) {
										console.error("Error saving reflection:", error);
										showNotification(
											"Failed to save reflection. Please try again.",
											"error",
										);
									}
								}}
								onContinue={goNext}
								subtitle="Review and edit your reflection description before continuing."
							/>
						</div>
					)}

					{currentStep === "contributing-factors-intro" && (
						<ContributingFactorsIntroStep onContinue={goNext} />
					)}

					{currentStep === "contributing-factors" && (
						<ContributingFactorsStep
							factors={contributingFactors}
							isLoading={isLoadingFactors}
							onUpdateFactor={(id, field, value) => {
								updateItem(
									contributingFactors,
									setContributingFactors,
									id,
									field,
									value,
								);
							}}
							onToggleFactor={(id) => {
								toggleSelection(
									contributingFactors,
									setContributingFactors,
									id,
								);
							}}
							onGenerateMore={generateContributingFactors}
							onAddNew={() => {
								addNewItem(
									contributingFactors,
									setContributingFactors,
									"factor",
								);
							}}
							onContinue={goNext}
						/>
					)}

					{currentStep === "signals-intro" && (
						<SignalsIntroStep onContinue={goNext} />
					)}

					{currentStep === "signals" && (
						<SignalsStep
							signals={signals}
							isLoading={isLoadingSignals}
							error={aiError}
							onUpdateSignal={(id, field, value) => {
								updateItem(signals, setSignals, id, field, value);
							}}
							onToggleSignal={(id) => {
								toggleSelection(signals, setSignals, id);
							}}
							onGenerateMore={generateSignals}
							onAddNew={() => {
								addNewItem(signals, setSignals, "signal");
							}}
							onContinue={goNext}
						/>
					)}

					{currentStep === "future-state" && (
						<FutureStateStep
							futureState={futureState}
							onFutureStateChange={setFutureState}
							onSave={async () => {
								try {
									await saveReflectionDraft("User saved future state");
									showNotification("Your Future State was saved");
								} catch (error) {
									console.error("Error saving reflection:", error);
									showNotification(
										"Failed to save reflection. Please try again.",
										"error",
									);
								}
							}}
							onContinue={goNext}
							isLoading={isLoadingFuture}
							subtitle="You can make edits as required"
						/>
					)}

					{currentStep === "corrective-actions-intro" && (
						<CorrectiveActionsIntroStep onContinue={goNext} />
					)}

					{currentStep === "corrective-actions" && (
						<CorrectiveActionsStep
							actions={correctiveActions}
							isLoading={isLoadingActions}
							error={aiError}
							onUpdateAction={(id, field, value) => {
								updateItem(
									correctiveActions,
									setCorrectiveActions,
									id,
									field,
									value,
								);
							}}
							onToggleAction={(id) => {
								toggleSelection(correctiveActions, setCorrectiveActions, id);
							}}
							onGenerateMore={generateCorrectiveActions}
							onAddNew={() => {
								addNewItem(correctiveActions, setCorrectiveActions, "action");
							}}
							onContinue={goNext}
						/>
					)}

					{currentStep === "linking" && (
						<LinkingStep
							actions={correctiveActions.filter((a) => a.selected)}
							factors={contributingFactors.filter((f) => f.selected)}
							signals={signals.filter((s) => s.selected)}
							actionLinks={actionLinks}
							currentLinkingIndex={currentLinkingIndex}
							onUpdateLinks={setActionLinks}
							onChangeIndex={setCurrentLinkingIndex}
							onContinue={goNext}
							onSaveCurrentLinks={handleSaveActionLinks}
							expandedItems={expandedItems}
							onToggleExpanded={(id) => {
								setExpandedItems((prev) => ({ ...prev, [id]: !prev[id] }));
							}}
						/>
					)}

					{currentStep === "summary" && (
						<SummaryStep
							title={reflectionTitle}
							description={reflectionDescription}
							contributingFactorsCount={getSelectedCountFactors(
								contributingFactors,
							)}
							signalsCount={getSelectedCountSignals(signals)}
							correctiveActionsCount={getSelectedCountActions(
								correctiveActions,
							)}
							isSaving={isSaving}
							isDraft={isDraft}
							onViewReport={() =>
								navigate(`/reflection/${currentReflectionId}`)
							}
							onReturnHome={() => navigate("/")}
							onViewReflections={() => navigate("/reflections")}
						/>
					)}
				</Card>
			</div>
		</div>
	);
}
